// react
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// router
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser } from '../../../App.context';
import {
	ContextLoans,
	ContextProject,
	ContextUserProjectPermissions,
	ContextUsersPermissions,
} from '../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2/ProjectV2.context';

// hooks
import { useContract } from '../../../hooks/useV2Contracts';

// components
import { PageDetailSkeleton } from '../../ui/PageDetailSkeleton/PageDetailSkeleton';
import {
	DialogDocumentViewer,
	ViewerDocumentInfo,
} from '../../dialogs/DialogDocumentViewer/DialogDocumentViewer';
import { DialogActions } from '../../dialogs/DialogActions/DialogActions';

// ui
import { BtnSplit } from '../../ui/BtnSplit/BtnSplit';

// mui
import {
	alpha,
	Alert,
	AlertTitle,
	Avatar,
	Box,
	Button,
	Chip,
	Collapse,
	Divider,
	Grid,
	IconButton,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// utils
import {
	getContractAmountForCurrency,
	getContractAmountsByCurrency,
	getContractCommitmentAmount,
	getContractNum,
} from '../ProjectV2/Contracts/Contracts.utils';

// icons
import {
	AccountTree as IconActivity,
	Add as IconAdd,
	AttachFile as IconAttachFile,
	Cancel as IconCancelled,
	CheckCircle as IconCheckCircle,
	DeleteOutlined as IconDelete,
	Description as IconContract,
	Edit as IconEdit,
	EditNote as IconEditNote,
	ExpandMore as IconExpandMore,
	GppGood as IconGate,
	History as IconHistory,
	HourglassEmpty as IconPending,
	Assignment as IconWorkPlan,
	OpenInNew as IconOpenInNew,
	Paid as IconPaid,
	Payment as IconPayment,
	PictureAsPdf as IconPdf,
	Receipt as IconReceipt,
	RadioButtonUnchecked as IconUnchecked,
	Schedule as IconSchedule,
	Verified as IconVerified,
	Group as IconGroup,
	Send as IconSend,
	ThumbUp as IconThumbUp,
	Undo as IconUndo,
} from '@mui/icons-material';

// utils
import { formatCurrency } from '../../../utils/currency';
import { resolveContractFxRate } from '../../../utils/fxRate';
import {
	canApprove as canApproveEntity,
	canCancel as canCancelEntity,
	canComplete as canCompleteEntity,
	canEdit as canEditEntity,
	canReopen as canReopenEntity,
	canReturn as canReturnEntity,
	canSubmit as canSubmitEntity,
	isApproved,
} from '../../../utils/makerChecker';

// ai api
import { fetchAiDocuments } from '../../../services/aiApi';
import { deactivateV2Contract } from '../../../services/api';
import type { AiDocument } from '../ProjectV2/Documents/documentTypes';

// data
import { COLORS, GRADIENTS } from '../../../data';

// config
import { featureFlags } from '../../../config/featureFlags';

// design system
import { spacing } from '../../../utils/designSystem';

// sse
import { useV2StatusListener } from '../../../utils/sse/useV2StatusListener';

// types
import type { ActionButton, Activity, Condition, Invoice } from '../../../@types';

// v2 components
import { CardDetail } from '../../cards/CardDetail/CardDetail';
import { CardInvoiceDetails } from '../../cards/CardInvoiceDetails/CardInvoiceDetails';
import { DataRequestFieldRow } from '../../ui/DataRequestFieldRow/DataRequestFieldRow';
import { DialogEntityDocuments } from '../../dialogs/DialogEntityDocuments/DialogEntityDocuments';
import {
	getStatusColor,
	getStatusLabel,
	HeaderDetailPage,
} from '../../ui/HeaderDetailPage/HeaderDetailPage';
import { TruncatedText } from '../../ui/TruncatedText/TruncatedText';
import { CardAiSummary } from '../../cards/CardAiSummary/CardAiSummary';
import { customFieldsToStats } from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';
import { Time } from '../../ui/Time/Time';

// dialogs
import { DialogAddInvoice } from '../../dialogs/DialogAddInvoice/DialogAddInvoice';
import { DialogAuditTimeline } from '../../dialogs/DialogAuditTimeline/DialogAuditTimeline';
import { DialogPrepareForPayment } from '../../dialogs/DialogPrepareForPayment/DialogPrepareForPayment';
import { DialogUploadDocument } from '../../dialogs/DialogUploadDocument/DialogUploadDocument';

// forms
import { FormAddCondition } from '../../forms/FormAddConditionWizard/FormAddConditionWizard';

// Field type display helpers (matching PageCondition)
const FIELD_TYPE_COLORS: Record<string, string> = {
	document: '#1976d2',
	yesno: '#7b1fa2',
	text: '#546e7a',
	number: '#00897b',
	date: '#e65100',
	select: '#5c6bc0',
};

const FIELD_TYPE_LABELS: Record<string, string> = {
	document: 'Document',
	yesno: 'Yes / No',
	text: 'Text',
	number: 'Number',
	date: 'Date',
	select: 'Dropdown',
};

export const PageContract = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { contractId } = useParams<{ contractId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const contractIdNumber = Number(contractId);

	// Fetch contract by ID
	const {
		data: contract,
		loading: contractLoading,
		refetch,
		update: updateContract,
	} = useContract({
		id: contractIdNumber,
		isEnabled: Number.isFinite(contractIdNumber),
		query: { populate: 'activity,supplier' },
	});

	// Track dialog state for refetch on close
	const dialogParam = searchParams.get('dialog');
	const prevDialogRef = useRef<string | null>(dialogParam);

	// Refetch data when dialog closes (dialog param goes from value to null)
	useEffect(() => {
		if (prevDialogRef.current && !dialogParam) {
			// Dialog was open and is now closed - refetch data
			refetch();
		}
		prevDialogRef.current = dialogParam;
	}, [dialogParam, refetch]);

	// Live updates: refetch this contract when someone else changes its status.
	useV2StatusListener({ resource: 'contract', id: contractIdNumber, onChange: refetch });

	// context
	const { setBanner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);
	const { project } = useContext(ContextProject);
	const { conditions, workPlans, vouchers, getInvoicesByContract } = useContext(ContextSpendPlan);
	const { loans: projectLoans } = useContext(ContextLoans);
	const { usersPermissions: projectUsers } = useContext(ContextUsersPermissions);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// state
	const [expandedConditions, setExpandedConditions] = useState<Set<number>>(new Set());
	const [contractDocs, setContractDocs] = useState<AiDocument[]>([]);
	const [conditionDocs, setConditionDocs] = useState<Record<number, AiDocument[]>>({});
	const [docsLoading, setDocsLoading] = useState(false);

	// Document viewer state
	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerDoc, setViewerDoc] = useState<{
		id: string;
		name: string;
		category: 'other';
		type: 'doc';
		size: string;
		uploadedAt: string;
		uploadedBy: string;
	} | null>(null);
	const [viewerBlobFilename, setViewerBlobFilename] = useState<string>('');
	const [viewerDocInfo, setViewerDocInfo] = useState<ViewerDocumentInfo | null>(null);
	const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);

	// Maker-Checker workflow state
	const [showSubmitDialog, setShowSubmitDialog] = useState(false);
	const [showApproveDialog, setShowApproveDialog] = useState(false);
	const [showReturnDialog, setShowReturnDialog] = useState(false);
	const [showCompleteDialog, setShowCompleteDialog] = useState(false);
	const [showCancelDialog, setShowCancelDialog] = useState(false);
	const [showReopenDialog, setShowReopenDialog] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [rejectionReason, setRejectionReason] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Audit log dialog visibility is controlled by the `audit=1` URL param so
	// the dialog state survives reloads / sharing the link.
	const auditTimelineOpen = searchParams.get('audit') === '1';
	const setAuditTimelineOpen = (open: boolean) => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (open) next.set('audit', '1');
				else next.delete('audit');
				return next;
			},
			{ replace: true }
		);
	};
	const [auditActivities, setAuditActivities] = useState<Activity[]>([]);
	const [auditLoading, setAuditLoading] = useState(false);

	// Fetch audit log activities for this contract only.
	const fetchAuditActivities = useCallback(async () => {
		if (!Number.isFinite(contractIdNumber)) return;
		setAuditLoading(true);
		try {
			const accessToken = sessionStorage.getItem('accessToken');
			const idToken = sessionStorage.getItem('idToken');
			const res = await fetch(
				`${process.env.REACT_APP_API_URL}/activities?contract=${contractIdNumber}&populate=actor&order=DESC&orderBy=created&limit=100&page=1`,
				{
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);
			if (res.ok) {
				const json = await res.json();
				setAuditActivities(json.data || []);
			}
		} catch (err) {
			console.error('Failed to fetch contract audit activities:', err);
		} finally {
			setAuditLoading(false);
		}
	}, [contractIdNumber]);

	useEffect(() => {
		fetchAuditActivities();
	}, [fetchAuditActivities, contract?.status]);

	// Fetch contract-level documents
	const fetchContractDocs = useCallback(async () => {
		if (!contractIdNumber || !Number.isFinite(contractIdNumber)) return;
		setDocsLoading(true);
		try {
			const response = await fetchAiDocuments({
				tags: `contract:${contractIdNumber}`,
				populate: 'uploaded_by,source',
				page_size: 50,
			});
			setContractDocs(response.data);
		} catch {
			// silent
		} finally {
			setDocsLoading(false);
		}
	}, [contractIdNumber]);

	useEffect(() => {
		fetchContractDocs();
	}, [fetchContractDocs]);

	// Fetch condition-level documents when conditions expand
	const fetchConditionDocs = useCallback(
		async (conditionId: number) => {
			if (conditionDocs[conditionId]) return; // already fetched
			try {
				const response = await fetchAiDocuments({
					source_type: 'condition',
					source_id: conditionId,
					populate: 'uploaded_by,source',
					page_size: 50,
				});
				setConditionDocs((prev) => ({ ...prev, [conditionId]: response.data }));
			} catch {
				setConditionDocs((prev) => ({ ...prev, [conditionId]: [] }));
			}
		},
		[conditionDocs]
	);

	// handlers
	const handleEdit = () => {
		if (!contract) return;
		// Contract edits are only allowed in DRAFT. After submission, any
		// change must go back through the maker-checker review/approval
		// workflow (return contract to DRAFT first).
		if (contract.status !== 'DRAFT') return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'editContract');
		params.set('dialogContract', String(contract.id));
		setSearchParams(params);
	};

	const handleProcessPayment = () => {
		if (!contract) return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'preparePayment');
		params.set('dialogContract', String(contract.id));
		setSearchParams(params);
	};

	// True when the URL is in "prepare payment" mode — the contract body
	// is replaced by the embedded prepare-payment view instead of opening
	// a modal dialog.
	const isPreparePaymentMode = searchParams.get('dialog') === 'preparePayment';

	const handleClosePreparePayment = useCallback(() => {
		const params = new URLSearchParams(searchParams);
		params.delete('dialog');
		params.delete('dialogContract');
		setSearchParams(params);
	}, [searchParams, setSearchParams]);

	const handlePreparePaymentSuccess = useCallback(() => {
		handleClosePreparePayment();
		setBanner({
			message: t('spendPlan.preparePayment.success', 'Payment voucher created successfully'),
			severity: 'success',
		});
	}, [handleClosePreparePayment, setBanner, t]);

	const handleAddInvoice = () => {
		if (!contract) return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'addInvoice');
		params.set('dialogContract', String(contract.id));
		setSearchParams(params);
	};

	const handleUploadDocument = () => {
		if (!contract) return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'uploadDocument');
		setSearchParams(params);
	};

	const handleDeleteContract = async () => {
		if (!contract) return;

		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');

		if (!accessToken || !idToken) {
			setBanner({ message: 'Not authenticated', severity: 'error' });
			return;
		}

		setIsSubmitting(true);
		const response = await deactivateV2Contract({ accessToken, idToken }, contract.id);
		setIsSubmitting(false);

		if (response.status === 200 || response.status === 204) {
			setShowDeleteDialog(false);
			setBanner({ message: 'Contract deactivated successfully.', severity: 'success' });
			navigate(`/project/${project?.idWbg}/spend-plans/contracts`);
			return;
		}

		setShowDeleteDialog(false);
		setBanner({
			message: `Failed to deactivate contract: ${response.error?.message || 'Unknown error'}`,
			severity: 'error',
		});
	};

	// Milestone (payment-condition) changes are only allowed while the
	// contract is in DRAFT and the user has contract-update permission.
	// Once SUBMITTED/ACTIVE/COMPLETED/CANCELLED the definitions are locked
	// and any change must go back through the review/approval workflow by
	// first returning the contract to DRAFT.
	const isContractEditable = canEditEntity({
		status: contract?.status,
		permissions: userProjectPermissions,
		resource: 'contract',
	});

	const handleAddCondition = () => {
		if (!contract) return;
		if (!isContractEditable) {
			setBanner({
				message: `Milestones can only be added while the contract is in DRAFT (current: ${contract.status}). Return the contract to DRAFT to make changes.`,
				severity: 'info',
			});
			return;
		}
		const params = new URLSearchParams(searchParams);
		params.delete('dialog');
		params.delete('dialogContract');
		params.delete('dialogWorkPlan');
		params.delete('dialogActivity');
		params.set('view', 'addCondition');
		setSearchParams(params);
	};

	const handleCloseConditionForm = () => {
		const params = new URLSearchParams(searchParams);
		params.delete('view');
		params.delete('editConditionId');
		params.delete('dialog');
		params.delete('dialogContract');
		params.delete('dialogWorkPlan');
		params.delete('dialogActivity');
		setSearchParams(params);
	};

	const handleConditionFormSuccess = () => {
		const isEdit = currentView === 'editCondition';
		handleCloseConditionForm();
		refetch();
		setBanner({
			message: isEdit
				? t('spendPlan.conditionUpdated', 'Condition updated successfully')
				: t('spendPlan.conditionCreated', 'Condition created successfully'),
			severity: 'success',
		});
	};

	const handleOpenConditionPage = (conditionId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/condition/${conditionId}`);
	};

	const handleOpenVoucherPage = (voucherId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/voucher/${voucherId}`);
	};

	// --- Maker-Checker Handlers ---

	const handleSubmitForApproval = async () => {
		if (!contract) return;
		setIsSubmitting(true);
		try {
			const res = await updateContract({ status: 'SUBMITTED' });
			if (res.error) {
				setBanner({ message: res.error.message, severity: 'error' });
			} else {
				setBanner({
					message: 'Contract submitted for approval.',
					severity: 'success',
				});
			}
			setShowSubmitDialog(false);
		} catch (err) {
			console.error('Failed to submit contract:', err);
			setBanner({ message: 'Failed to submit contract. Please try again.', severity: 'error' });
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleApprove = async () => {
		if (!contract) return;
		setIsSubmitting(true);
		try {
			const res = await updateContract({ status: 'ACTIVE' });
			if (res.error) {
				setBanner({ message: res.error.message, severity: 'error' });
			} else {
				setBanner({ message: 'Contract approved.', severity: 'success' });
			}
			setShowApproveDialog(false);
		} catch (err) {
			console.error('Failed to approve contract:', err);
			setBanner({ message: 'Failed to approve contract. Please try again.', severity: 'error' });
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleReturn = async () => {
		if (!contract) return;
		setIsSubmitting(true);
		try {
			const res = await updateContract({
				status: 'DRAFT',
				rejectionReason: rejectionReason || undefined,
			});
			if (res.error) {
				setBanner({ message: res.error.message, severity: 'error' });
			} else {
				setBanner({ message: 'Contract returned to draft.', severity: 'info' });
			}
			setShowReturnDialog(false);
			setRejectionReason('');
		} catch (err) {
			console.error('Failed to return contract:', err);
			setBanner({ message: 'Failed to return contract. Please try again.', severity: 'error' });
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleComplete = async () => {
		if (!contract) return;
		setIsSubmitting(true);
		try {
			const res = await updateContract({ status: 'COMPLETED' });
			if (res.error) {
				setBanner({ message: res.error.message, severity: 'error' });
			} else {
				setBanner({ message: 'Contract marked as completed.', severity: 'success' });
			}
			setShowCompleteDialog(false);
		} catch (err) {
			console.error('Failed to complete contract:', err);
			setBanner({ message: 'Failed to complete contract. Please try again.', severity: 'error' });
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCancelContract = async () => {
		if (!contract) return;
		setIsSubmitting(true);
		try {
			const res = await updateContract({ status: 'CANCELLED' });
			if (res.error) {
				setBanner({ message: res.error.message, severity: 'error' });
			} else {
				setBanner({ hasClose: true, message: 'Contract cancelled.', severity: 'warning' });
				navigate(`/project/${project?.idWbg}/spend-plans/contracts`);
			}
			setShowCancelDialog(false);
		} catch (err) {
			console.error('Failed to cancel contract:', err);
			setBanner({ message: 'Failed to cancel contract. Please try again.', severity: 'error' });
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleReopen = async () => {
		if (!contract) return;
		setIsSubmitting(true);
		try {
			const res = await updateContract({ status: 'DRAFT' });
			if (res.error) {
				setBanner({ message: res.error.message, severity: 'error' });
			} else {
				setBanner({ message: 'Contract reopened as draft.', severity: 'info' });
			}
			setShowReopenDialog(false);
		} catch (err) {
			console.error('Failed to reopen contract:', err);
			setBanner({ message: 'Failed to reopen contract. Please try again.', severity: 'error' });
		} finally {
			setIsSubmitting(false);
		}
	};

	// Open document in the viewer dialog
	const handleOpenDocViewer = async (documentId: string, documentName: string) => {
		if (!documentName) return;
		setViewerDoc({
			id: documentId,
			name: documentName,
			category: 'other',
			type: 'doc',
			size: '',
			uploadedAt: '',
			uploadedBy: '',
		});
		setViewerBlobFilename(documentName);
		setViewerDocInfo({ name: documentName });
		setViewerOpen(true);
		try {
			const res = await fetchAiDocuments({
				search: documentName,
				project_id: project?.id,
				populate: 'uploaded_by,source',
			});
			const aiDoc = res.data?.find((d: AiDocument) => d.id === documentId) || res.data?.[0];
			if (aiDoc) {
				const metadata = aiDoc.metadata || {};
				const uploaderName =
					typeof aiDoc.uploaded_by === 'object' && aiDoc.uploaded_by
						? aiDoc.uploaded_by.displayName || aiDoc.uploaded_by.email
						: String(metadata.uploaded_by_name || '');
				const sizeBytes = Number(metadata.file_size_bytes || 0);
				const sizeStr =
					sizeBytes > 0
						? `${parseFloat((sizeBytes / Math.pow(1024, Math.floor(Math.log(sizeBytes) / Math.log(1024)))).toFixed(1))} ${['B', 'KB', 'MB', 'GB'][Math.floor(Math.log(sizeBytes) / Math.log(1024))]}`
						: undefined;
				setViewerDocInfo({
					id: aiDoc.id,
					name: aiDoc.filename,
					jobId: aiDoc.job_id || undefined,
					tags:
						Array.isArray(metadata.tags) && metadata.tags.length > 0
							? (metadata.tags as string[])
							: undefined,
					sourceType: (metadata.source_type as string) || undefined,
					sourceLabel: aiDoc.source?.name || aiDoc.contract_name || undefined,
					size: sizeStr,
					uploadedAt: aiDoc.uploaded_at,
					uploadedBy: uploaderName || '',
				});
			}
		} catch {
			// Metadata fetch failed — viewer still works
		}
	};

	// Inline condition form mode (URL-driven, mirrors isPreparePaymentMode pattern)
	const currentView = searchParams.get('view');
	const editConditionId = searchParams.get('editConditionId');
	const isConditionFormOpen = currentView === 'addCondition' || currentView === 'editCondition';
	const editCondition = editConditionId
		? conditions.find((c) => c.id === Number(editConditionId)) || null
		: null;

	// Get base conditions from work plan (gates/prerequisites that apply to all contracts)
	const baseConditions = useMemo(() => {
		if (!contract) return [];
		return conditions.filter((c) => {
			const condWorkPlan = typeof c.workPlan === 'number' ? c.workPlan : c.workPlan?.id;
			// Base conditions: belong to the work plan and have conditionType 'base'
			return condWorkPlan === contract.workPlan && c.conditionType === 'base';
		});
	}, [conditions, contract]);

	// Get payment conditions specific to this contract (payment milestones)
	// Exclude template-derived conditions (those with sourceCondition set)
	const paymentConditions = useMemo(() => {
		if (!contract) return [];
		return conditions.filter((c) => {
			const condContract = typeof c.contract === 'number' ? c.contract : c.contract?.id;
			return condContract === contract.id && c.conditionType === 'payment' && !c.sourceCondition;
		});
	}, [conditions, contract]);

	// Lookup maps fed to the documents dialog so condition/field IDs encoded in
	// blob tags (condition:N / requirement_field:X) render as human labels.
	const conditionNamesById = useMemo(() => {
		const map: Record<number, string> = {};
		[...baseConditions, ...paymentConditions].forEach((c) => {
			if (c.name) map[c.id] = c.name;
		});
		return map;
	}, [baseConditions, paymentConditions]);

	const fieldLabelsById = useMemo(() => {
		const map: Record<string, string> = {};
		[...baseConditions, ...paymentConditions].forEach((c) => {
			(c.verificationSections || []).forEach((s) => {
				(s.fields || []).forEach((f) => {
					if (f.label) map[f.id] = f.label;
				});
			});
		});
		return map;
	}, [baseConditions, paymentConditions]);

	// Track which conditions have been paid (have vouchers that are REDEEMED)
	const paidConditionIds = useMemo(() => {
		const ids = new Set<number>();
		vouchers.forEach((v) => {
			// Only count as paid if voucher is REDEEMED
			if (v.status !== 'REDEEMED') return;
			const conditionId = typeof v.condition === 'number' ? v.condition : v.condition?.id;
			if (conditionId) {
				ids.add(conditionId);
			}
		});
		return ids;
	}, [vouchers]);

	// Track which conditions have pending vouchers (created but not yet paid)
	const pendingVoucherConditionIds = useMemo(() => {
		const ids = new Set<number>();
		vouchers.forEach((v) => {
			// Pending voucher = exists but not REDEEMED or CANCELLED
			if (v.status === 'REDEEMED' || v.status === 'CANCELLED') return;
			const conditionId = typeof v.condition === 'number' ? v.condition : v.condition?.id;
			if (conditionId) {
				ids.add(conditionId);
			}
		});
		return ids;
	}, [vouchers]);

	// Track which conditions have had cancelled vouchers (but no active/paid ones)
	const cancelledVoucherConditionIds = useMemo(() => {
		const ids = new Set<number>();
		vouchers.forEach((v) => {
			if (v.status !== 'CANCELLED') return;
			const conditionId = typeof v.condition === 'number' ? v.condition : v.condition?.id;
			if (conditionId) {
				ids.add(conditionId);
			}
		});
		// Remove IDs that also have paid or pending vouchers (those take precedence)
		paidConditionIds.forEach((id) => ids.delete(id));
		pendingVoucherConditionIds.forEach((id) => ids.delete(id));
		return ids;
	}, [vouchers, paidConditionIds, pendingVoucherConditionIds]);

	// Get vouchers for this contract
	const contractVouchers = useMemo(() => {
		if (!contract) return [];
		return vouchers.filter((v) => {
			const vContractId = typeof v.contract === 'number' ? v.contract : v.contract?.id;
			return vContractId === contract.id;
		});
	}, [contract, vouchers]);

	// GENERATED vouchers represent in-flight drafts (data being gathered
	// before a payment is prepared). We surface them in a banner so PIU
	// users can find and resume work the supplier or they themselves saved.
	const contractDrafts = useMemo(
		() => contractVouchers.filter((v) => v.status === 'GENERATED'),
		[contractVouchers]
	);

	// Sum of voucher amounts that are "pending" — created but not yet REDEEMED
	// (and not CANCELLED). Surfaced in the financial summary row so users can
	// see in-flight payments without affecting the headline paid % (which is
	// driven by REDEEMED only, computed server-side as `contract.totalPaid`).
	const contractVouchersPending = useMemo(() => {
		if (!contract) return 0;
		const wp = workPlans.find((e) => e.id === contract.workPlan);
		const commitmentForCalc = getContractCommitmentAmount(contract, wp) ?? 0;
		return contractVouchers.reduce((sum, v) => {
			if (v.status === 'REDEEMED' || v.status === 'CANCELLED') return sum;
			const condId = typeof v.condition === 'number' ? v.condition : v.condition?.id;
			const voucherCondition = condId ? conditions.find((c) => c.id === condId) : null;
			const amount =
				v.amount ||
				voucherCondition?.paymentAmount ||
				(voucherCondition?.paymentPercentage
					? (commitmentForCalc * voucherCondition.paymentPercentage) / 100
					: 0);
			return sum + amount;
		}, 0);
	}, [conditions, contract, contractVouchers, workPlans]);

	// Per-loan breakdown of the contract's REDEEMED vouchers. Sums each
	// voucher's financingSources rows (amount + tax = gross paid to that
	// loan) and groups by loanId. Vouchers without a financingSources
	// array fall back to a single-entry attribution against voucher.loan
	// so legacy single-loan payments still show up in the breakdown.
	// Surfaced via the Paid stat's tooltip on the contract header (Phase
	// 5.5 of story 150) so a multi-loan contract reveals the split on
	// hover without leaving the page.
	const contractPaidByLoan = useMemo(() => {
		const sums = new Map<number, number>();
		contractVouchers.forEach((v) => {
			if (v.status !== 'REDEEMED') return;
			const fs = v.financingSources;
			if (Array.isArray(fs) && fs.length > 0) {
				fs.forEach((s) => {
					const gross = Number(s.amount || 0) + Number(s.tax || 0);
					if (gross === 0) return;
					sums.set(s.loanId, (sums.get(s.loanId) || 0) + gross);
				});
				return;
			}
			// Legacy fallback: pre-150 vouchers carry only voucher.loan.
			const legacyLoanId = typeof v.loan === 'number' ? v.loan : v.loan?.id;
			if (legacyLoanId == null) return;
			const gross = Number(v.amount || 0) + Number(v.taxWithheld || 0);
			if (gross === 0) return;
			sums.set(legacyLoanId, (sums.get(legacyLoanId) || 0) + gross);
		});
		return Array.from(sums.entries()).map(([loanId, amount]) => {
			const loan = projectLoans.find((l) => l.id === loanId);
			const loanName =
				loan?.code ||
				(loan?.financier && loan?.num ? `${loan.financier}${loan.num}` : '') ||
				loan?.name ||
				null;
			return { loanId, loanName, amount };
		});
	}, [contractVouchers, projectLoans]);

	// Map condition ID → vouchers for inline display
	const vouchersByCondition = useMemo(() => {
		const map = new Map<number, typeof contractVouchers>();
		contractVouchers.forEach((v) => {
			const condId = typeof v.condition === 'number' ? v.condition : v.condition?.id;
			if (condId) {
				const arr = map.get(condId) || [];
				arr.push(v);
				map.set(condId, arr);
			}
		});
		return map;
	}, [contractVouchers]);

	// Map condition ID → flat array of all field responses from voucher sectionResponses
	const responsesByCondition = useMemo(() => {
		const map = new Map<
			number,
			Map<
				string,
				{
					value: string | null;
					documentId?: string;
					documentName?: string;
					fieldType?: string;
					overridden?: boolean;
					overrideComment?: string;
					answeredAt?: string;
				}
			>
		>();
		contractVouchers.forEach((v) => {
			const condId = typeof v.condition === 'number' ? v.condition : v.condition?.id;
			if (!condId || !v.sectionResponses) return;
			v.sectionResponses.forEach((sr) => {
				if (sr.conditionId !== condId) return;
				if (!map.has(condId)) map.set(condId, new Map());
				const fieldMap = map.get(condId);
				if (!fieldMap) return;
				(sr.fieldResponses || []).forEach((r) => {
					// Keep the latest response for each field
					if (!fieldMap.has(r.fieldId) || r.answeredAt) {
						fieldMap.set(r.fieldId, {
							value: r.value,
							documentId: r.documentId,
							documentName: r.documentName,
							fieldType: r.fieldType,
							overridden: r.overridden,
							overrideComment: r.overrideComment,
							answeredAt: r.answeredAt,
						});
					}
				});
			});
		});
		return map;
	}, [contractVouchers]);

	// Map condition ID → Map<sectionId, latest VerificationSectionStatus> derived
	// from voucher sectionResponses (the PIU verify/reject actions in Voucher.tsx
	// write here, not back into condition.verificationSections). The latest
	// non-null verifiedAt/rejectedAt wins so re-rejections don't lose ground.
	const sectionStatusByCondition = useMemo(() => {
		const map = new Map<number, Map<string, 'PENDING' | 'VERIFIED' | 'REJECTED'>>();
		contractVouchers.forEach((v) => {
			if (!v.sectionResponses) return;
			v.sectionResponses.forEach((sr) => {
				if (!sr.conditionId || !sr.sectionId) return;
				if (!map.has(sr.conditionId)) map.set(sr.conditionId, new Map());
				const sectionMap = map.get(sr.conditionId);
				if (!sectionMap) return;
				const existing = sectionMap.get(sr.sectionId);
				// Prefer a non-PENDING status over PENDING; otherwise keep first writer.
				if (!existing || (existing === 'PENDING' && sr.status && sr.status !== 'PENDING')) {
					sectionMap.set(
						sr.sectionId,
						(sr.status as 'PENDING' | 'VERIFIED' | 'REJECTED') || 'PENDING'
					);
				}
			});
		});
		return map;
	}, [contractVouchers]);

	// Invoices submitted against this contract, grouped by the payment
	// milestone they target (invoice.milestoneNumber === condition.id, the
	// condition's stable primary key). Lets each milestone row surface its
	// own invoice card(s).
	const invoicesByMilestoneNumber = useMemo(() => {
		const map = new Map<number, Invoice[]>();
		if (!contract) return map;
		getInvoicesByContract(contract.id).forEach((inv) => {
			if (inv.milestoneNumber != null) {
				const list = map.get(inv.milestoneNumber) || [];
				list.push(inv);
				map.set(inv.milestoneNumber, list);
			}
		});
		return map;
	}, [contract, getInvoicesByContract]);

	// Supplier-side handoff state for a GENERATED voucher (an in-flight
	// draft shared between the supplier portal and the PIU). A voucher is
	// "Supplier Submitted" once every recorded section response is in
	// SUBMITTED status — that's the signal the supplier has handed work
	// back to the PIU for review. Otherwise the supplier is still drafting.
	// Returns null for non-GENERATED vouchers (those have their own status).
	const getSupplierVoucherState = (
		voucher: { status?: string; sectionResponses?: Array<{ status?: string }> } | null | undefined
	): 'SUBMITTED' | 'DRAFT' | null => {
		if (!voucher || voucher.status !== 'GENERATED') return null;
		const responses = voucher.sectionResponses || [];
		if (responses.length === 0) return 'DRAFT';
		return responses.every((s) => (s.status as string) === 'SUBMITTED') ? 'SUBMITTED' : 'DRAFT';
	};

	const toggleConditionExpand = (conditionId: number) => {
		setExpandedConditions((prev) => {
			const next = new Set(prev);
			if (next.has(conditionId)) {
				next.delete(conditionId);
			} else {
				next.add(conditionId);
				// Fetch condition docs when expanding
				fetchConditionDocs(conditionId);
			}
			return next;
		});
	};

	// Not found state (only after loading is complete)
	if (!contractLoading && !contract) {
		return (
			<Box sx={{ px: 3, py: 4 }}>
				<Typography>Contract not found</Typography>
			</Box>
		);
	}

	// Get work plan for currency (safe access for loading state)
	const workPlan = contract ? workPlans.find((e) => e.id === contract.workPlan) : undefined;
	const workPlanCurrency = workPlan?.currency || 'USD';

	// The contract's commitmentAmountLocal is denominated in the project's
	// local currency, so the local restatement caption uses that code. Falls
	// back to the work plan's local currency when the project carries none.
	const projectLocalCurrency =
		project?.currencies?.find((c) => c.type === 'LOCAL')?.currency || workPlan?.currencyLocal;

	// Implied local-per-reporting rate. The canonical resolver picks the
	// contract's own basis first (commitment scalars, then the multi-currency
	// amount rows when the scalars are blank — a USD+ETB contract carries its
	// true rate in those rows and that rate is *not necessarily* the work
	// plan's) and only falls back to the work-plan rate when the contract
	// itself has nothing to offer. The resolver returns a typed FxRate whose
	// `source` tag feeds the rate-source tooltip on every restated figure.
	const contractFx = resolveContractFxRate(contract, workPlan, project);
	const contractBudgetRate = contractFx?.rate ?? null;

	// Render a payment figure in the cross-currency equivalent (e.g. "₱999.00"
	// when the headline is USD, or "$600.00" when the headline is ETB) using
	// the contract's implied budget rate. Returns undefined when there's no
	// rate to apply or only one distinct currency on the contract, in which
	// case the caption is simply omitted (the headline carries the only
	// meaningful figure).
	const localEquivalent = (reportingValue: number): string | undefined => {
		if (contractBudgetRate == null || !projectLocalCurrency) return undefined;
		if (contractPrimaryCurrency === projectLocalCurrency) {
			if (!workPlanCurrency || workPlanCurrency === projectLocalCurrency) return undefined;
			if (contractBudgetRate === 0) return undefined;
			return formatCurrency({
				currency: workPlanCurrency,
				value: reportingValue / contractBudgetRate,
			});
		}
		return formatCurrency({
			currency: projectLocalCurrency,
			value: reportingValue * contractBudgetRate,
		});
	};

	// Paid-side figures (paid, pending, V1, per-loan) arrive denominated in the
	// reporting currency — vouchers are created there. When the contract's
	// display primary currency is the *local* currency (e.g. a BRL contract on
	// a USD-reporting project), these must be restated into that primary
	// currency so they line up with the contract value and the paid % is a
	// like-for-like ratio. Identity when the primary already IS the reporting
	// currency, so single-currency / USD-reporting contracts are unaffected.
	const reportingToPrimary = (reportingValue: number): number => {
		if (!contractFx || contractBudgetRate == null) return reportingValue;
		if (contractPrimaryCurrency === contractFx.localCurrency) {
			return reportingValue * contractBudgetRate;
		}
		return reportingValue;
	};

	// Local-currency caption for a value that is denominated in the *reporting*
	// currency (voucher amounts). Unlike `localEquivalent` — which expects its
	// input in the contract's primary currency — this restates reporting → local
	// directly (× budget rate). Returns undefined when there's no rate or only a
	// single currency in play.
	const reportingToLocalCaption = (reportingValue: number): string | undefined => {
		if (!contractFx || contractBudgetRate == null || !projectLocalCurrency) return undefined;
		if (contractFx.reportingCurrency === contractFx.localCurrency) return undefined;
		return formatCurrency({
			currency: projectLocalCurrency,
			value: reportingValue * contractBudgetRate,
		});
	};

	// A contract is denominated in its own currency, which is not necessarily
	// the work plan's currency. The header and per-condition payment maths use
	// the contract's primary amount currency so labels match the figures.
	const contractAmountsByCurrency = contract ? getContractAmountsByCurrency(contract) : [];
	const contractPrimaryCurrency = contractAmountsByCurrency[0]?.currency || workPlanCurrency;

	// Basis for percentage-based milestone amounts. Milestones are denominated in
	// the contract's own currency (matching the header), so resolve the
	// commitment/contract amount in the contract's primary currency rather than
	// the work plan's. An explicit commitmentAmount is still honored when present.
	const milestoneBasis = contract
		? (getContractCommitmentAmount(contract, { currency: contractPrimaryCurrency }) ?? 0)
		: 0;

	// Stats for payment conditions - only count as "paid" if voucher is REDEEMED
	const paidPayments = paymentConditions.filter((c) => paidConditionIds.has(c.id)).length;
	// Count conditions with pending vouchers (created but not yet redeemed)
	const pendingVoucherPayments = paymentConditions.filter((c) =>
		pendingVoucherConditionIds.has(c.id)
	).length;
	// Count verified/approved conditions that are ready but have no voucher yet
	const verifiedPayments = paymentConditions.filter(
		(c) =>
			!paidConditionIds.has(c.id) &&
			!pendingVoucherConditionIds.has(c.id) &&
			(c.status === 'APPROVED' || c.status === 'VERIFIED')
	).length;
	const pendingPayments = paymentConditions.filter(
		(c) =>
			(c.status === 'PENDING' || c.status === 'SUBMITTED') &&
			!paidConditionIds.has(c.id) &&
			!pendingVoucherConditionIds.has(c.id)
	).length;

	// Check whether a condition has unfulfilled required requirement fields
	const hasUnfulfilledRequirements = (condition: Condition) => {
		const fields = (condition.verificationSections || []).flatMap((s) => s.fields || []);
		const requiredFields = fields.filter((f) => f.required);
		if (requiredFields.length === 0) return false;
		const condResponses = responsesByCondition.get(condition.id);
		if (!condResponses) return true; // no responses at all — all required fields unfulfilled
		return requiredFields.some((f) => {
			const r = condResponses.get(f.id);
			return !r || (r.value === null && !r.documentId);
		});
	};

	// Stats for base conditions (work plan-level gates)
	// A base condition is "met" if status is APPROVED/VERIFIED AND has no unfulfilled requirements
	const metBaseConditions = baseConditions.filter(
		(c) => (c.status === 'APPROVED' || c.status === 'VERIFIED') && !hasUnfulfilledRequirements(c)
	).length;
	const totalBaseConditions = baseConditions.length;

	// Payment stats for prerequisites that have payment terms
	const baseVoucherCount = baseConditions.reduce(
		(sum, c) => sum + (vouchersByCondition.get(c.id) || []).length,
		0
	);
	const baseConditionsWithPayment = baseConditions.filter(
		(c) => c.paymentAmount !== null || c.paymentPercentage !== null
	).length;
	const prereqSubtitle = `${metBaseConditions}/${totalBaseConditions} fulfilled${
		baseVoucherCount > 0
			? ` \u2022 ${baseVoucherCount} voucher${baseVoucherCount !== 1 ? 's' : ''}`
			: baseConditionsWithPayment > 0
				? ` \u2022 ${baseConditionsWithPayment} with payment`
				: ''
	}`;

	const getConditionStatusInfo = (status: string, isPayment = false, condition?: Condition) => {
		// "Action Needed" only makes sense once the contract is past the
		// definition/approval phase. While the contract is still DRAFT (or
		// SUBMITTED awaiting checker approval), milestones aren't actionable
		// yet — the PIU is still shaping the contract itself, so flagging
		// individual conditions as needing action is misleading.
		const contractActionable =
			!!contract && contract.status !== 'DRAFT' && contract.status !== 'SUBMITTED';
		if (contractActionable && condition && hasUnfulfilledRequirements(condition)) {
			return {
				color: COLORS.warning.main,
				icon: IconSchedule,
				label: t('condition.actionNeeded'),
				tooltip: 'Complete required data requests to proceed',
			};
		}
		switch (status) {
			case 'APPROVED':
				return {
					color: COLORS.success.main,
					icon: IconCheckCircle,
					label: isPayment ? t('condition.disbursed') : t('condition.fulfilled'),
					tooltip: isPayment
						? 'Payment has been disbursed'
						: 'All requirements have been satisfied',
				};
			case 'VERIFIED':
				return {
					color: isPayment ? COLORS.info.main : COLORS.success.main,
					icon: isPayment ? IconVerified : IconCheckCircle,
					label: isPayment ? t('condition.readyForPayment') : t('condition.fulfilled'),
					tooltip: isPayment
						? 'Verified and eligible for payment'
						: 'All requirements have been satisfied',
				};
			case 'SUBMITTED':
				return {
					color: COLORS.info.main,
					icon: IconSchedule,
					label: t('condition.underReview'),
					tooltip: 'Submitted and awaiting verification',
				};
			case 'REJECTED':
				return {
					color: theme.palette.error.main,
					icon: IconUnchecked,
					label: t('condition.returned'),
					tooltip: 'Returned \u2014 review feedback and resubmit',
				};
			default:
				return {
					color: theme.palette.grey[400],
					icon: IconPending,
					label: t('condition.notStarted'),
					tooltip: 'No action has been taken on this condition yet',
				};
		}
	};

	// Build stats for HeaderDetailPage (only when contract is loaded)
	const contractStatusColors = contract ? getStatusColor(contract.status) : null;

	const headerStats = contract
		? [
				// Status
				{
					label: t('status'),
					value: (
						<Chip
							label={getStatusLabel(contract.status, t)}
							size="small"
							sx={{
								bgcolor: contractStatusColors?.bg,
								color: contractStatusColors?.text,
								fontSize: { xs: '1rem', sm: '1.125rem' },
								fontWeight: 600,
								height: 28,
								px: 1,
							}}
						/>
					),
				},
				// Last update — most-recent activity row touching the contract,
				// its conditions, or its vouchers. Surfaces "new material to
				// review" for the PIU and "PIU has acted" for the supplier
				// without firing a notification per save.
				...(contract.lastUpdate?.at
					? [
							{
								label: t('lastUpdate'),
								value: (
									<Stack direction="row" alignItems="center" spacing={0.75}>
										<Tooltip title={contract.lastUpdate.actor?.name || 'Unknown'} arrow>
											<Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem' }}>
												{(contract.lastUpdate.actor?.name || '?').charAt(0).toUpperCase()}
											</Avatar>
										</Tooltip>
										<Tooltip title={new Date(contract.lastUpdate.at).toLocaleString()} arrow>
											<span>
												<Time date={contract.lastUpdate.at} />
											</span>
										</Tooltip>
									</Stack>
								),
							},
						]
					: []),
				// Work Plan
				...(workPlan
					? [
							{
								label: t('workPlan'),
								value: (
									<Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
										<IconWorkPlan
											sx={{
												color: GRADIENTS.contract.icon,
												fontSize: { xs: 18, sm: 20 },
												flexShrink: 0,
											}}
										/>
										<TruncatedText text={workPlan.name} component="span" sx={{ maxWidth: 200 }} />
										<IconOpenInNew
											sx={{
												color: GRADIENTS.contract.subtitle,
												fontSize: { xs: 14, sm: 16 },
												flexShrink: 0,
											}}
										/>
									</Stack>
								),
								to: `/project/${project?.idWbg}/spend-plans/work-planning/work-plan/${workPlan.id}`,
							},
						]
					: []),
				// Activity
				...(contract.planningActivity
					? [
							{
								label: t('activity'),
								value: (
									<Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
										<IconActivity
											sx={{
												color: GRADIENTS.contract.icon,
												fontSize: { xs: 18, sm: 20 },
												flexShrink: 0,
											}}
										/>
										<TruncatedText
											text={contract.activityName}
											component="span"
											sx={{ maxWidth: 200 }}
										/>
										<IconOpenInNew
											sx={{
												color: GRADIENTS.contract.subtitle,
												fontSize: { xs: 14, sm: 16 },
												flexShrink: 0,
											}}
										/>
									</Stack>
								),
								to: `/project/${project?.idWbg}/spend-plans/activity/${contract.planningActivity}`,
							},
						]
					: []),
				// Supplier
				{
					label: t('supplier'),
					value: contract.entities?.find((e) => e.type === 'SUPPLIER')?.name || t('na'),
				},
				// Milestones Paid
				{
					label: t('milestonesPaid'),
					value: (
						<Tooltip
							title={`${paidPayments} paid, ${verifiedPayments} verified, ${pendingVoucherPayments} voucher pending, ${pendingPayments} remaining`}
							arrow>
							<span>{`${paidPayments}/${paymentConditions.length}`}</span>
						</Tooltip>
					),
				},
				// Verified awaiting payment
				...(verifiedPayments > 0
					? [
							{
								label: t('contract.verifiedAwaitingPayment'),
								value: (
									<Tooltip title="Conditions verified and eligible for voucher creation" arrow>
										<span>{String(verifiedPayments)}</span>
									</Tooltip>
								),
							},
						]
					: []),
				// Pending vouchers
				...(pendingVoucherPayments > 0
					? [
							{
								label: t('contract.voucherPending'),
								value: (
									<Tooltip title="Vouchers created and awaiting review or disbursement" arrow>
										<span>{String(pendingVoucherPayments)}</span>
									</Tooltip>
								),
							},
						]
					: []),
				// Custom fields
				...customFieldsToStats(
					(project?.customFieldDefinitions || []).find((d) => d.entityType === 'contract')
						?.definitions || [],
					contract.customData || {}
				),
			]
		: [];

	return (
		<Box>
			{/* Header - Full width, always visible with loading state */}
			<HeaderDetailPage
				loading={contractLoading}
				loadingActions={2}
				icon={IconContract}
				breadcrumb={{
					label: 'Contracts',
					to: `/project/${project?.idWbg}/spend-plans/contracts`,
				}}
				title={contract ? getContractNum(contract) : ''}
				variant="contract"
				stats={headerStats}
				financialSummary={
					contract
						? {
								contractData: {
									currency: contractPrimaryCurrency,
									contractValue:
										getContractAmountForCurrency(contract, contractPrimaryCurrency) ?? 0,
									commitmentAmount: contract.commitmentAmount,
									paid: reportingToPrimary(contract.totalPaidReporting ?? contract.totalPaid ?? 0),
									paidByLoan: contractPaidByLoan.map((l) => ({
										...l,
										amount: reportingToPrimary(l.amount),
									})),
									paidV1: reportingToPrimary(contract.totalPaidV1 || 0),
									pending: reportingToPrimary(contractVouchersPending),
									additionalAmounts: contractAmountsByCurrency
										.slice(1)
										.map(({ currency, amount }) => ({ currency, amount })),
									workPlanCurrency,
									currencyLocal: projectLocalCurrency,
									currencyReporting: workPlanCurrency,
									fx: contractFx,
								},
							}
						: undefined
				}
				customAction={
					!contractLoading && contract ? (
						isPreparePaymentMode || isConditionFormOpen ? (
							// When the contract body is replaced by an embedded form
							// (prepare-payment or add/edit condition), the contract-level
							// actions (Edit / Approve / Documents / etc.) no longer apply.
							// Surface a single Cancel CTA that clears the URL params and
							// returns the user to the default contract view. The embedded
							// form's own Submit / Save buttons remain in the form body.
							<Button
								size="small"
								variant="outlined"
								startIcon={<IconCancelled />}
								onClick={
									isPreparePaymentMode ? handleClosePreparePayment : handleCloseConditionForm
								}>
								{t('cancel', 'Cancel')}
							</Button>
						) : (
							<BtnSplit
								size="small"
								actions={(() => {
									const gateInput = {
										status: contract.status,
										permissions: userProjectPermissions,
										resource: 'contract' as const,
										makerUserId: contract.submittedBy,
										currentUserId: user?.id ?? null,
									};

									// Primary action varies by status:
									//   DRAFT     → Edit
									//   SUBMITTED → Approve (checker) or Return (maker)
									//   ACTIVE    → Prepare a Payment
									//   else      → Documents
									const list: ActionButton[] = [];

									if (isApproved(contract.status)) {
										list.push({
											children: t('contract.preparePayment'),
											onClick: handleProcessPayment,
											startIcon: <IconPayment />,
										});
										list.push({
											children: t('contract.addInvoice'),
											onClick: handleAddInvoice,
											startIcon: <IconReceipt />,
										});
									}

									if (canEditEntity(gateInput)) {
										list.push({
											children: t('edit'),
											onClick: handleEdit,
											startIcon: <IconEdit />,
										});
									}
									if (canSubmitEntity(gateInput)) {
										list.push({
											children: t('submitForApproval'),
											onClick: () => setShowSubmitDialog(true),
											startIcon: <IconSend />,
										});
									}
									if (canApproveEntity(gateInput)) {
										list.push({
											children: t('approve'),
											onClick: () => setShowApproveDialog(true),
											startIcon: <IconThumbUp />,
										});
									}
									if (canReturnEntity(gateInput)) {
										list.push({
											children: t('recall'),
											onClick: () => setShowReturnDialog(true),
											startIcon: <IconUndo />,
										});
									}

									// Allow returning a contract to DRAFT from either a
									// terminal state (Reopen) or the live/approved state,
									// where the action is surfaced as "Edit Contract" so
									// users don't have to first Cancel just to make
									// changes. In both cases the contract goes back
									// through the review/approval workflow before
									// becoming active again.
									if (canReopenEntity(gateInput, { fromApproved: true })) {
										list.push({
											children: isApproved(contract.status)
												? t('contract.editContract')
												: t('contract.reopenAsDraft'),
											onClick: () => setShowReopenDialog(true),
											startIcon: isApproved(contract.status) ? <IconEdit /> : <IconUndo />,
										});
									}

									// Documents is available in every status
									list.push({
										children: t('documents'),
										onClick: () => setDocumentsDialogOpen(true),
										startIcon: <IconContract />,
									});

									// Audit log is available in every status
									list.push({
										children: t('viewAuditLog'),
										onClick: () => setAuditTimelineOpen(true),
										startIcon: <IconHistory />,
									});

									// ACTIVE → COMPLETED / CANCELLED
									if (canCompleteEntity(gateInput)) {
										list.push({
											children: t('contract.markCompleted'),
											onClick: () => setShowCompleteDialog(true),
											startIcon: <IconCheckCircle />,
										});
									}
									if (canCancelEntity(gateInput)) {
										list.push({
											children: t('contract.cancelContract'),
											onClick: () => setShowCancelDialog(true),
											startIcon: <IconCancelled />,
										});
									}

									if (canEditEntity(gateInput)) {
										list.push({
											children: t('contract.deactivateContract'),
											onClick: () => setShowDeleteDialog(true),
											startIcon: <IconDelete />,
											destructive: true,
											dividerBefore: true,
										});
									}

									return list;
								})()}
							/>
						)
					) : undefined
				}
			/>

			{/* Content with padding */}
			<Box
				sx={{
					pb: spacing.xl,
					px: { xs: spacing.sm, sm: spacing.md, md: spacing.lg },
					pt: spacing.md,
					bgcolor: '#F8F9FB',
					minHeight: '100vh',
				}}>
				{/* Show skeleton for content while loading */}
				{contractLoading ? (
					<PageDetailSkeleton leftCards={4} rightCards={2} />
				) : contract && isPreparePaymentMode ? (
					<DialogPrepareForPayment
						contract={contract}
						isOpen
						variant="page"
						onClose={handleClosePreparePayment}
						onSuccess={handlePreparePaymentSuccess}
						onDocClick={handleOpenDocViewer}
					/>
				) : contract && isConditionFormOpen ? (
					<Box sx={{ bgcolor: '#fff', borderRadius: 2, p: { xs: 2, sm: 3 } }}>
						<FormAddCondition
							condition={editCondition}
							onClose={handleCloseConditionForm}
							onSuccess={handleConditionFormSuccess}
							preSelectedContext={{
								activityId: contract.planningActivity,
								contractId: contract.id,
								workPlanId: contract.workPlan,
							}}
						/>
					</Box>
				) : contract ? (
					<>
						{/* Surfacing GENERATED (draft) vouchers so PIU can find and resume
						    saved payment data — whether the supplier started it on the EXT
						    portal or the PIU saved a draft themselves. Hidden while the
						    embedded prepare-payment view is open since the user is already
						    resuming the draft. */}
						{contractDrafts.length > 0 && (
							<Alert
								severity="info"
								icon={<IconEditNote />}
								sx={{ mb: 2, alignItems: 'center' }}
								action={
									<Button
										size="small"
										variant="contained"
										color="info"
										onClick={handleProcessPayment}>
										{t('continueDraft', 'Continue Draft')}
									</Button>
								}>
								<AlertTitle sx={{ mb: 0.25 }}>
									{contractDrafts.length === 1
										? t('draftsInProgress', '1 draft in progress', {
												count: contractDrafts.length,
											})
										: t('draftsInProgress', '{{count}} drafts in progress', {
												count: contractDrafts.length,
											}).replace('{{count}}', String(contractDrafts.length))}
								</AlertTitle>
								<Typography variant="body2">
									{t(
										'draftsInProgressDescription',
										'Saved milestone data will be preserved when you prepare a payment.'
									)}
								</Typography>
							</Alert>
						)}

						{/* AI Summary Card — full width above the two-column layout */}
						<CardAiSummary
							contract={contract}
							contractDocs={contractDocs}
							docsLoading={docsLoading}
							onUpdate={updateContract}
						/>

						{/* Two-column layout: Conditions left (8/12) + Documents right (4/12) */}
						<Grid container spacing={2}>
							{/* Left Column: Prerequisites + Milestones */}
							<Grid item xs={12} lg={8}>
								{/* Prerequisites (only shown when base conditions exist and feature flag enabled) */}
								{featureFlags.PREREQUISITES && baseConditions.length > 0 && (
									<Box sx={{ mb: 2 }}>
										<CardDetail
											icon={IconGate}
											title="Prerequisites"
											subtitle={prereqSubtitle}
											variant="condition"
											themeColor={GRADIENTS.condition.icon}
											onAdd={isContractEditable ? handleAddCondition : undefined}
											tooltipAdd="Add Prerequisite"
											emptyMessage="No prerequisites">
											{baseConditions.length > 0 && (
												<Stack spacing={0}>
													{baseConditions
														.sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
														.map((condition, idx) => {
															const isPaid = paidConditionIds.has(condition.id);
															const hasPendingVoucher = pendingVoucherConditionIds.has(
																condition.id
															);
															const hasCancelledVoucher = cancelledVoucherConditionIds.has(
																condition.id
															);
															const isMet =
																(condition.status === 'APPROVED' ||
																	condition.status === 'VERIFIED' ||
																	isPaid) &&
																!hasUnfulfilledRequirements(condition);
															const statusInfo = getConditionStatusInfo(
																condition.status,
																false,
																condition
															);
															const conditionVouchers = vouchersByCondition.get(condition.id) || [];
															const isExpanded = expandedConditions.has(condition.id);
															const hasPaymentTerms =
																condition.paymentAmount !== null ||
																condition.paymentPercentage !== null;
															const paymentAmount =
																condition.paymentAmount ||
																(condition.paymentPercentage
																	? (milestoneBasis * condition.paymentPercentage) / 100
																	: 0);
															const hasExpandableContent =
																conditionVouchers.length > 0 ||
																(condition.verificationSections &&
																	condition.verificationSections.length > 0);
															// Voucher-aware status (same logic as milestones)
															const pendingVoucher = conditionVouchers.find(
																(v) => v.status !== 'REDEEMED' && v.status !== 'CANCELLED'
															);
															const prereqSupplierState = getSupplierVoucherState(pendingVoucher);
															const StatusIcon = isPaid
																? IconPaid
																: hasPendingVoucher
																	? IconVerified
																	: hasCancelledVoucher
																		? IconCancelled
																		: statusInfo.icon;
															const prereqStatusColor = isPaid
																? COLORS.success.main
																: hasPendingVoucher
																	? prereqSupplierState === 'SUBMITTED'
																		? COLORS.warning.main
																		: COLORS.info.main
																	: hasCancelledVoucher
																		? COLORS.error.main
																		: statusInfo.color;
															const prereqStatusLabel = isPaid
																? 'Paid'
																: hasPendingVoucher
																	? pendingVoucher?.status === 'VERIFIED'
																		? 'Awaiting Review'
																		: pendingVoucher?.status === 'READY'
																			? 'Awaiting Payment'
																			: prereqSupplierState === 'SUBMITTED'
																				? 'Supplier Submitted'
																				: 'Supplier Draft'
																	: hasCancelledVoucher
																		? 'Cancelled'
																		: statusInfo.label;
															const prereqStatusTooltip = isPaid
																? 'Payment has been disbursed'
																: hasPendingVoucher
																	? pendingVoucher?.status === 'VERIFIED'
																		? 'Conditions verified \u2014 awaiting review'
																		: pendingVoucher?.status === 'READY'
																			? 'Approved \u2014 awaiting payment processing'
																			: prereqSupplierState === 'SUBMITTED'
																				? 'Supplier has submitted \u2014 review required'
																				: 'Supplier is still drafting their submission'
																	: hasCancelledVoucher
																		? 'Voucher was cancelled'
																		: isMet
																			? 'Verified and eligible for payment'
																			: statusInfo.tooltip;
															return (
																<Box
																	key={condition.id}
																	sx={{
																		bgcolor:
																			idx % 2 === 1
																				? alpha(theme.palette.grey[100], 0.5)
																				: 'transparent',
																	}}>
																	<Divider />
																	<Stack
																		direction="row"
																		alignItems="center"
																		spacing={1.5}
																		onClick={() =>
																			hasExpandableContent && toggleConditionExpand(condition.id)
																		}
																		sx={{
																			px: 2,
																			py: 1.25,
																			cursor: hasExpandableContent ? 'pointer' : 'default',
																			'&:hover': hasExpandableContent
																				? { bgcolor: alpha(GRADIENTS.condition.icon, 0.04) }
																				: {},
																		}}>
																		{/* Expand chevron */}
																		<Box
																			sx={{
																				width: 28,
																				display: 'flex',
																				alignItems: 'center',
																				justifyContent: 'center',
																				flexShrink: 0,
																			}}>
																			{hasExpandableContent && (
																				<Box
																					sx={{
																						transition: 'transform 0.2s',
																						transform: isExpanded
																							? 'rotate(180deg)'
																							: 'rotate(0deg)',
																						display: 'flex',
																						alignItems: 'center',
																					}}>
																					<IconExpandMore
																						sx={{ fontSize: 20, color: theme.palette.grey[500] }}
																					/>
																				</Box>
																			)}
																		</Box>
																		{/* Status icon */}
																		<Tooltip title={prereqStatusLabel} arrow>
																			<Box
																				sx={{
																					width: 32,
																					height: 32,
																					borderRadius: '50%',
																					bgcolor: alpha(prereqStatusColor, 0.12),
																					display: 'flex',
																					alignItems: 'center',
																					justifyContent: 'center',
																					flexShrink: 0,
																				}}>
																				<StatusIcon
																					sx={{ fontSize: 18, color: prereqStatusColor }}
																				/>
																			</Box>
																		</Tooltip>
																		{/* Name + description */}
																		<Box sx={{ flex: 1, minWidth: 0 }}>
																			<Stack direction="row" alignItems="center" spacing={1}>
																				<Typography
																					sx={{
																						fontWeight: 600,
																						fontSize: '1.25rem',
																						color: isMet ? '#6B7280' : '#111827',
																						textDecoration: isMet ? 'line-through' : 'none',
																					}}
																					noWrap>
																					{condition.name}
																				</Typography>
																				<Tooltip title="Open condition" arrow>
																					<IconButton
																						size="small"
																						onClick={(e) => {
																							e.stopPropagation();
																							handleOpenConditionPage(condition.id);
																						}}
																						sx={{
																							p: 0.25,
																							opacity: 0.4,
																							'&:hover': { opacity: 1 },
																						}}>
																						<IconOpenInNew sx={{ fontSize: 16 }} />
																					</IconButton>
																				</Tooltip>
																				{isContractEditable && (
																					<Tooltip title="Edit condition" arrow>
																						<IconButton
																							size="small"
																							onClick={(e) => {
																								e.stopPropagation();
																								const params = new URLSearchParams(searchParams);
																								params.set('view', 'editCondition');
																								params.set('editConditionId', String(condition.id));
																								setSearchParams(params);
																							}}
																							sx={{
																								p: 0.25,
																								opacity: 0.4,
																								'&:hover': { opacity: 1 },
																							}}>
																							<IconEdit sx={{ fontSize: 16 }} />
																						</IconButton>
																					</Tooltip>
																				)}
																			</Stack>
																			{condition.description && (
																				<Typography
																					sx={{ color: '#9CA3AF', fontSize: '1.25rem' }}
																					noWrap>
																					{condition.description}
																				</Typography>
																			)}
																		</Box>
																		{/* Percentage badge */}
																		{condition.paymentPercentage && (
																			<Chip
																				label={`${condition.paymentPercentage}%`}
																				size="small"
																				sx={{
																					bgcolor: alpha(COLORS.primary.main, 0.08),
																					color: COLORS.primary.main,
																					fontWeight: 700,
																					fontSize: '1.25rem',
																					height: 28,
																				}}
																			/>
																		)}
																		{/* Amount (if any) */}
																		{hasPaymentTerms && (
																			<Box sx={{ textAlign: 'right' }}>
																				<Typography
																					sx={{
																						fontWeight: 500,
																						fontSize: '1.25rem',
																						color: '#374151',
																						whiteSpace: 'nowrap',
																					}}>
																					{formatCurrency({
																						currency: contractPrimaryCurrency,
																						value: paymentAmount,
																					})}
																				</Typography>
																				{localEquivalent(paymentAmount) && (
																					<Typography
																						sx={{
																							fontWeight: 500,
																							fontSize: '1.15rem',
																							color: '#6b7280',
																							whiteSpace: 'nowrap',
																						}}>
																						{localEquivalent(paymentAmount)}
																					</Typography>
																				)}
																			</Box>
																		)}
																		{/* Status chip */}
																		<Tooltip
																			title={prereqStatusTooltip || ''}
																			arrow
																			disableHoverListener={!prereqStatusTooltip}>
																			<Chip
																				icon={
																					isPaid ? (
																						<IconPaid sx={{ fontSize: '16px !important' }} />
																					) : hasPendingVoucher ? (
																						<IconVerified sx={{ fontSize: '16px !important' }} />
																					) : hasCancelledVoucher ? (
																						<IconCancelled sx={{ fontSize: '16px !important' }} />
																					) : (
																						<statusInfo.icon sx={{ fontSize: '16px !important' }} />
																					)
																				}
																				label={prereqStatusLabel}
																				size="small"
																				sx={{
																					bgcolor: alpha(prereqStatusColor, 0.1),
																					color: prereqStatusColor,
																					fontWeight: 600,
																					fontSize: '1.25rem',
																					height: 28,
																				}}
																			/>
																		</Tooltip>
																	</Stack>
																	{/* ─── Expanded detail panel ─── */}
																	<Collapse in={isExpanded}>
																		<Box
																			sx={{
																				borderLeft: `3px solid ${alpha(prereqStatusColor, 0.25)}`,
																				ml: '33px',
																				borderRadius: '0 0 0 8px',
																				py: 0.5,
																			}}>
																			{/* ── Vouchers ── */}
																			{conditionVouchers.length > 0 && (
																				<Box>
																					<Box
																						sx={{
																							display: 'flex',
																							alignItems: 'center',
																							gap: 0.75,
																							pl: 3,
																							pr: 2,
																							pt: 0.75,
																							pb: 0.25,
																						}}>
																						<Typography
																							sx={{
																								fontSize: '1.1rem',
																								fontWeight: 700,
																								color: theme.palette.grey[400],
																								textTransform: 'uppercase',
																								letterSpacing: '0.1em',
																							}}>
																							Vouchers
																						</Typography>
																						<Typography
																							sx={{
																								fontSize: '1.1rem',
																								fontWeight: 700,
																								color: theme.palette.grey[400],
																							}}>
																							{conditionVouchers.length}
																						</Typography>
																					</Box>
																					{conditionVouchers
																						.sort(
																							(a, b) =>
																								new Date(b.created).getTime() -
																								new Date(a.created).getTime()
																						)
																						.map((voucher) => {
																							const vIsPaid = voucher.status === 'REDEEMED';
																							const vIsCancelled = voucher.status === 'CANCELLED';
																							const recipientName =
																								typeof voucher.recipientEntity === 'object' &&
																								voucher.recipientEntity?.name
																									? voucher.recipientEntity.name
																									: null;
																							const contractAmountForCalc = milestoneBasis;
																							const voucherAmount =
																								voucher.amount ||
																								condition.paymentAmount ||
																								(condition.paymentPercentage
																									? (contractAmountForCalc *
																											condition.paymentPercentage) /
																										100
																									: 0);
																							const vStatusColor = vIsPaid
																								? COLORS.success.main
																								: vIsCancelled
																									? COLORS.error.main
																									: voucher.status === 'VERIFIED'
																										? COLORS.info.main
																										: voucher.status === 'READY'
																											? GRADIENTS.condition.icon
																											: COLORS.warning.main;
																							const vSupplierState =
																								getSupplierVoucherState(voucher);
																							const vStatusLabel = vIsPaid
																								? 'Paid'
																								: vIsCancelled
																									? 'Cancelled'
																									: voucher.status === 'VERIFIED'
																										? 'Awaiting Review'
																										: voucher.status === 'READY'
																											? 'Awaiting Payment'
																											: vSupplierState === 'SUBMITTED'
																												? 'Supplier Submitted'
																												: vSupplierState === 'DRAFT'
																													? 'Supplier Draft'
																													: 'Processing';
																							return (
																								<Stack
																									key={voucher.id}
																									direction="row"
																									alignItems="center"
																									spacing={1}
																									onClick={() => handleOpenVoucherPage(voucher.id)}
																									sx={{
																										cursor: 'pointer',
																										py: 0.5,
																										px: 2,
																										pl: 3,
																										'&:hover': {
																											bgcolor: alpha(
																												GRADIENTS.financial.icon,
																												0.04
																											),
																										},
																									}}>
																									<Tooltip title={vStatusLabel} arrow>
																										<Box
																											sx={{
																												width: 8,
																												height: 8,
																												borderRadius: '50%',
																												bgcolor: vStatusColor,
																												flexShrink: 0,
																											}}
																										/>
																									</Tooltip>
																									<Typography
																										sx={{
																											fontWeight: 500,
																											fontSize: '1.25rem',
																											color: '#374151',
																											flex: 1,
																											minWidth: 0,
																										}}
																										noWrap>
																										Voucher #{voucher.id}
																										{recipientName
																											? ` · ${recipientName}`
																											: ''}{' '}
																										<Typography
																											component="span"
																											sx={{
																												color: '#9CA3AF',
																												fontSize: '1.25rem',
																											}}>
																											{new Date(
																												voucher.created
																											).toLocaleDateString()}
																										</Typography>
																									</Typography>
																									<Box sx={{ textAlign: 'right', flexShrink: 0 }}>
																										<Typography
																											sx={{
																												fontWeight: 600,
																												fontSize: '1.25rem',
																												color: '#374151',
																											}}>
																											{formatCurrency({
																												currency:
																													voucher.currency || workPlanCurrency,
																												value: voucherAmount,
																											})}
																										</Typography>
																										{(voucher.currency || workPlanCurrency) ===
																											workPlanCurrency &&
																											reportingToLocalCaption(voucherAmount) && (
																												<Typography
																													sx={{
																														fontWeight: 500,
																														fontSize: '1.15rem',
																														color: '#6b7280',
																													}}>
																													{reportingToLocalCaption(voucherAmount)}
																												</Typography>
																											)}
																									</Box>
																									<Chip
																										label={vStatusLabel}
																										size="small"
																										sx={{
																											bgcolor: alpha(vStatusColor, 0.08),
																											color: vStatusColor,
																											fontWeight: 600,
																											fontSize: '1.25rem',
																											height: 24,
																											minWidth: 60,
																										}}
																									/>
																								</Stack>
																							);
																						})}
																				</Box>
																			)}
																			{/* ── Requirements Sections ── */}
																			{condition.verificationSections &&
																				condition.verificationSections.length > 0 &&
																				(() => {
																					const condResponses = responsesByCondition.get(
																						condition.id
																					);
																					return (
																						<Box>
																							<Divider sx={{ opacity: 0.15, mx: 2, my: 0.5 }} />
																							{condition.verificationSections.map(
																								(section, sIdx) => {
																									const assignee = projectUsers.find(
																										(u) => u.id === section.assignedTo
																									);
																									// Section status reflects the PIU's per-section
																									// verify/reject action written into voucher
																									// sectionResponses; falls back to the section
																									// definition's status (PENDING by default).
																									const effectiveSectionStatus =
																										sectionStatusByCondition
																											.get(condition.id)
																											?.get(section.id) ||
																										section.status ||
																										'PENDING';
																									const statusColor =
																										effectiveSectionStatus === 'VERIFIED'
																											? COLORS.success.main
																											: effectiveSectionStatus === 'REJECTED'
																												? theme.palette.error.main
																												: theme.palette.grey[500];
																									const sectionFields = section.fields || [];
																									const answeredCount = sectionFields.filter(
																										(f) => {
																											const r = condResponses?.get(f.id);
																											return (
																												r && (r.value !== null || r.documentId)
																											);
																										}
																									).length;
																									return (
																										<Box key={section.id}>
																											<Box
																												sx={{
																													display: 'flex',
																													alignItems: 'center',
																													gap: 0.75,
																													pl: 3,
																													pr: 2,
																													pt: 0.75,
																													pb: 0.25,
																												}}>
																												<IconGroup
																													sx={{
																														fontSize: 16,
																														color: 'primary.main',
																													}}
																												/>
																												<Typography
																													sx={{
																														fontSize: '1.1rem',
																														fontWeight: 700,
																														color: theme.palette.grey[500],
																														textTransform: 'uppercase',
																														letterSpacing: '0.1em',
																													}}>
																													{section.name || `Section ${sIdx + 1}`}
																												</Typography>
																												{assignee && (
																													<Chip
																														label={`${assignee.firstName} ${assignee.lastName}`}
																														size="small"
																														variant="outlined"
																														sx={{ height: 24, fontSize: '1.1rem' }}
																													/>
																												)}
																												<Typography
																													sx={{
																														fontSize: '1.1rem',
																														fontWeight: 700,
																														color:
																															answeredCount ===
																																sectionFields.length &&
																															sectionFields.length > 0
																																? COLORS.success.main
																																: theme.palette.grey[500],
																													}}>
																													{answeredCount}/{sectionFields.length}
																												</Typography>
																												<Chip
																													label={getStatusLabel(
																														effectiveSectionStatus,
																														t
																													)}
																													size="small"
																													variant="outlined"
																													sx={{
																														height: 24,
																														fontSize: '1.1rem',
																														color: statusColor,
																														borderColor: statusColor,
																													}}
																												/>
																											</Box>
																											{sectionFields.map((field) => {
																												const response = condResponses?.get(
																													field.id
																												);
																												const hasResponse =
																													!!response &&
																													(response.value !== null ||
																														response.documentId);
																												const isDocField =
																													field.fieldType === 'document';
																												const hasDoc =
																													isDocField &&
																													response?.documentId &&
																													response?.documentName;
																												const accentColor =
																													FIELD_TYPE_COLORS[field.fieldType] ||
																													'#546e7a';
																												return (
																													<Stack
																														key={field.id}
																														direction="row"
																														alignItems="center"
																														spacing={1}
																														sx={{
																															py: 0.5,
																															px: 2,
																															pl: 4,
																															cursor: hasDoc
																																? 'pointer'
																																: 'default',
																															'&:hover': hasDoc
																																? {
																																		bgcolor: alpha(
																																			COLORS.primary.main,
																																			0.03
																																		),
																																	}
																																: {},
																														}}
																														onClick={
																															hasDoc
																																? () =>
																																		handleOpenDocViewer(
																																			response?.documentId ?? '',
																																			response?.documentName ?? ''
																																		)
																																: undefined
																														}>
																														<Box
																															sx={{
																																width: 8,
																																height: 8,
																																borderRadius: '50%',
																																bgcolor: hasResponse
																																	? COLORS.success.main
																																	: alpha(accentColor, 0.4),
																																flexShrink: 0,
																															}}
																														/>
																														<Typography
																															sx={{
																																fontSize: '1.25rem',
																																fontWeight: 500,
																																color: hasResponse
																																	? '#374151'
																																	: '#9CA3AF',
																																minWidth: 0,
																																flexShrink: 1,
																															}}
																															noWrap>
																															{field.label ||
																																FIELD_TYPE_LABELS[field.fieldType]}
																														</Typography>
																														<Typography
																															sx={{
																																color: '#D1D5DB',
																																fontSize: '1.25rem',
																																flexShrink: 0,
																															}}>
																															—
																														</Typography>
																														<Box
																															sx={{
																																flex: 1,
																																minWidth: 0,
																																display: 'flex',
																																alignItems: 'center',
																																gap: 0.5,
																															}}>
																															{hasDoc ? (
																																<Typography
																																	sx={{
																																		fontSize: '1.25rem',
																																		color: COLORS.primary.main,
																																		fontWeight: 500,
																																		'&:hover': {
																																			textDecoration: 'underline',
																																		},
																																	}}
																																	noWrap>
																																	{response?.documentName}
																																</Typography>
																															) : hasResponse ? (
																																<Typography
																																	sx={{
																																		fontSize: '1.25rem',
																																		color: '#374151',
																																		fontWeight: 500,
																																	}}
																																	noWrap>
																																	{response?.overridden
																																		? `Override: ${response?.overrideComment || '—'}`
																																		: field.fieldType === 'yesno'
																																			? [
																																					'yes',
																																					'true',
																																					'1',
																																				].includes(
																																					String(
																																						response?.value ?? ''
																																					).toLowerCase()
																																				)
																																				? 'Yes'
																																				: 'No'
																																			: response?.value || '—'}
																																</Typography>
																															) : (
																																<Typography
																																	sx={{
																																		fontSize: '1.25rem',
																																		color: '#D1D5DB',
																																		fontStyle: 'italic',
																																	}}>
																																	{field.required
																																		? 'Required'
																																		: 'Optional'}
																																</Typography>
																															)}
																														</Box>
																														<Typography
																															sx={{
																																fontSize: '1.25rem',
																																color: accentColor,
																																fontWeight: 600,
																																flexShrink: 0,
																																opacity: 0.7,
																															}}>
																															{FIELD_TYPE_LABELS[field.fieldType] ||
																																field.fieldType}
																														</Typography>
																														{hasResponse ? (
																															<IconCheckCircle
																																sx={{
																																	fontSize: 16,
																																	color: COLORS.success.main,
																																	flexShrink: 0,
																																}}
																															/>
																														) : field.required ? (
																															<Box
																																sx={{
																																	width: 16,
																																	height: 16,
																																	borderRadius: '50%',
																																	border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
																																	flexShrink: 0,
																																}}
																															/>
																														) : (
																															<Box
																																sx={{ width: 16, flexShrink: 0 }}
																															/>
																														)}
																													</Stack>
																												);
																											})}
																										</Box>
																									);
																								}
																							)}
																						</Box>
																					);
																				})()}
																		</Box>
																	</Collapse>
																</Box>
															);
														})}
												</Stack>
											)}
										</CardDetail>
									</Box>
								)}

								{/* Payment Milestones */}
								<CardDetail
									icon={IconPayment}
									title={t('contract.paymentMilestones')}
									subtitle={t('contract.milestoneSubtitle', {
										count: contractVouchers.length,
										paid: paidPayments,
										total: paymentConditions.length,
									})}
									variant="condition"
									themeColor={GRADIENTS.financial.icon}
									headerActions={
										isContractEditable ? (
											<Tooltip title={t('contract.addMilestone')}>
												<IconButton
													onClick={handleAddCondition}
													size="small"
													sx={{ color: 'text.secondary' }}>
													<IconAdd sx={{ fontSize: 18 }} />
												</IconButton>
											</Tooltip>
										) : null
									}
									emptyMessage={t('contract.noPaymentMilestones')}>
									{paymentConditions.length > 0 && (
										<>
											<Stack spacing={0}>
												{paymentConditions
													.sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
													.map((condition, idx) => {
														const isPaid = paidConditionIds.has(condition.id);
														const hasPendingVoucher = pendingVoucherConditionIds.has(condition.id);
														const hasCancelledVoucher = cancelledVoucherConditionIds.has(
															condition.id
														);
														const isMet =
															(condition.status === 'APPROVED' ||
																condition.status === 'VERIFIED' ||
																isPaid) &&
															!hasUnfulfilledRequirements(condition);
														const statusInfo = getConditionStatusInfo(
															condition.status,
															true,
															condition
														);
														const hasPaymentTerms =
															condition.paymentAmount !== null ||
															condition.paymentPercentage !== null;
														const paymentAmount =
															condition.paymentAmount ||
															(condition.paymentPercentage
																? (milestoneBasis * condition.paymentPercentage) / 100
																: 0);
														const conditionVouchers = vouchersByCondition.get(condition.id) || [];
														const StatusIcon = isPaid
															? IconPaid
															: hasPendingVoucher
																? IconVerified
																: hasCancelledVoucher
																	? IconCancelled
																	: statusInfo.icon;
														const isExpanded = expandedConditions.has(condition.id);
														const hasExpandableContent =
															conditionVouchers.length > 0 ||
															(invoicesByMilestoneNumber.get(condition.id) || [])
																.length > 0 ||
															(condition.verificationSections &&
																condition.verificationSections.length > 0);
														// Status color & label — derived from condition status + voucher state
														const pendingVoucher = conditionVouchers.find(
															(v) => v.status !== 'REDEEMED' && v.status !== 'CANCELLED'
														);
														const supplierState = getSupplierVoucherState(pendingVoucher);
														const milestoneStatusColor = isPaid
															? COLORS.success.main
															: hasPendingVoucher
																? supplierState === 'SUBMITTED'
																	? COLORS.warning.main
																	: COLORS.info.main
																: hasCancelledVoucher
																	? COLORS.error.main
																	: isMet
																		? COLORS.info.main
																		: statusInfo.color;
														const milestoneStatusLabel = isPaid
															? 'Paid'
															: hasPendingVoucher
																? pendingVoucher?.status === 'VERIFIED'
																	? 'Awaiting Review'
																	: pendingVoucher?.status === 'READY'
																		? 'Awaiting Payment'
																		: supplierState === 'SUBMITTED'
																			? 'Supplier Submitted'
																			: 'Supplier Draft'
																: hasCancelledVoucher
																	? 'Cancelled'
																	: isMet
																		? 'Ready to Pay'
																		: statusInfo.label;
														const milestoneStatusTooltip = isPaid
															? 'Payment has been disbursed'
															: hasPendingVoucher
																? pendingVoucher?.status === 'VERIFIED'
																	? 'Conditions verified \u2014 awaiting review'
																	: pendingVoucher?.status === 'READY'
																		? 'Approved \u2014 awaiting payment processing'
																		: supplierState === 'SUBMITTED'
																			? 'Supplier has submitted \u2014 review required'
																			: 'Supplier is still drafting their submission'
																: hasCancelledVoucher
																	? 'Voucher was cancelled'
																	: isMet
																		? 'Verified and eligible for payment'
																		: statusInfo.tooltip;

														return (
															<Box
																key={condition.id}
																sx={{
																	bgcolor:
																		idx % 2 === 1
																			? alpha(theme.palette.grey[100], 0.5)
																			: 'transparent',
																}}>
																<Divider />
																<Stack
																	direction="row"
																	alignItems="center"
																	spacing={1.5}
																	onClick={() =>
																		hasExpandableContent && toggleConditionExpand(condition.id)
																	}
																	sx={{
																		px: 2,
																		py: 1.25,
																		cursor: hasExpandableContent ? 'pointer' : 'default',
																		'&:hover': hasExpandableContent
																			? { bgcolor: alpha(GRADIENTS.condition.icon, 0.04) }
																			: {},
																	}}>
																	{/* Expand chevron */}
																	<Box
																		sx={{
																			width: 28,
																			display: 'flex',
																			alignItems: 'center',
																			justifyContent: 'center',
																			flexShrink: 0,
																		}}>
																		{hasExpandableContent && (
																			<Box
																				sx={{
																					transition: 'transform 0.2s',
																					transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
																					display: 'flex',
																					alignItems: 'center',
																				}}>
																				<IconExpandMore
																					sx={{ fontSize: 20, color: theme.palette.grey[500] }}
																				/>
																			</Box>
																		)}
																	</Box>
																	{/* Status icon badge */}
																	<Tooltip
																		title={`#${condition.sequenceOrder || '–'} — ${milestoneStatusLabel}`}
																		arrow
																		placement="left">
																		<Box
																			sx={{
																				width: 32,
																				height: 32,
																				borderRadius: '50%',
																				bgcolor: alpha(milestoneStatusColor, 0.12),
																				display: 'flex',
																				alignItems: 'center',
																				justifyContent: 'center',
																				flexShrink: 0,
																			}}>
																			<StatusIcon
																				sx={{ fontSize: 18, color: milestoneStatusColor }}
																			/>
																		</Box>
																	</Tooltip>
																	{/* Name + milestone type + description */}
																	<Box sx={{ flex: 1, minWidth: 0 }}>
																		<Stack direction="row" alignItems="center" spacing={1}>
																			<Typography
																				sx={{
																					fontWeight: 600,
																					fontSize: '1.25rem',
																					color: isPaid ? '#6B7280' : '#111827',
																					textDecoration: isPaid ? 'line-through' : 'none',
																				}}
																				noWrap>
																				{condition.name}
																			</Typography>
																			<Tooltip title="Open condition" arrow>
																				<IconButton
																					size="small"
																					onClick={(e) => {
																						e.stopPropagation();
																						handleOpenConditionPage(condition.id);
																					}}
																					sx={{ p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}>
																					<IconOpenInNew sx={{ fontSize: 16 }} />
																				</IconButton>
																			</Tooltip>
																			{isContractEditable && (
																				<Tooltip title="Edit condition" arrow>
																					<IconButton
																						size="small"
																						onClick={(e) => {
																							e.stopPropagation();
																							const params = new URLSearchParams(searchParams);
																							params.set('view', 'editCondition');
																							params.set('editConditionId', String(condition.id));
																							setSearchParams(params);
																						}}
																						sx={{
																							p: 0.25,
																							opacity: 0.4,
																							'&:hover': { opacity: 1 },
																						}}>
																						<IconEdit sx={{ fontSize: 16 }} />
																					</IconButton>
																				</Tooltip>
																			)}
																			{condition.milestoneType && (
																				<Chip
																					label={
																						condition.milestoneType.charAt(0) +
																						condition.milestoneType.slice(1).toLowerCase()
																					}
																					size="small"
																					sx={{
																						bgcolor: alpha(COLORS.info.main, 0.08),
																						color: COLORS.info.main,
																						fontWeight: 600,
																						fontSize: '1.25rem',
																						height: 24,
																					}}
																				/>
																			)}
																		</Stack>
																		{condition.description && (
																			<Typography
																				sx={{ color: '#9CA3AF', fontSize: '1.25rem' }}
																				noWrap>
																				{condition.description}
																			</Typography>
																		)}
																	</Box>
																	{/* Percentage badge */}
																	{condition.paymentPercentage && (
																		<Chip
																			label={`${condition.paymentPercentage}%`}
																			size="small"
																			sx={{
																				bgcolor: alpha(COLORS.primary.main, 0.08),
																				color: COLORS.primary.main,
																				fontWeight: 700,
																				fontSize: '1.25rem',
																				height: 28,
																			}}
																		/>
																	)}
																	{/* Amount */}
																	{hasPaymentTerms && (
																		<Box sx={{ textAlign: 'right', flexShrink: 0 }}>
																			<Typography
																				sx={{
																					fontWeight: 500,
																					fontSize: '1.25rem',
																					color: '#374151',
																					whiteSpace: 'nowrap',
																				}}>
																				{formatCurrency({
																					currency: contractPrimaryCurrency,
																					value: paymentAmount,
																				})}
																			</Typography>
																			{localEquivalent(paymentAmount) && (
																				<Typography
																					sx={{
																						fontWeight: 500,
																						fontSize: '1.15rem',
																						color: '#6b7280',
																						whiteSpace: 'nowrap',
																					}}>
																					{localEquivalent(paymentAmount)}
																				</Typography>
																			)}
																		</Box>
																	)}
																	{/* Section review rollup */}
																	{(() => {
																		const totalSections =
																			condition.verificationSections?.length || 0;
																		if (totalSections === 0) return null;
																		const statusMap = sectionStatusByCondition.get(condition.id);
																		let verified = 0;
																		let rejected = 0;
																		(condition.verificationSections || []).forEach((s) => {
																			const st = statusMap?.get(s.id) || s.status || 'PENDING';
																			if (st === 'VERIFIED') verified++;
																			else if (st === 'REJECTED') rejected++;
																		});
																		const rollupColor =
																			rejected > 0
																				? theme.palette.error.main
																				: verified === totalSections
																					? COLORS.success.main
																					: theme.palette.grey[500];
																		const rollupLabel =
																			rejected > 0
																				? `${verified}/${totalSections} ✓ · ${rejected} ✗`
																				: `${verified}/${totalSections} ✓`;
																		return (
																			<Tooltip
																				title={`Section reviews — verified ${verified}, rejected ${rejected}, total ${totalSections}`}
																				arrow>
																				<Chip
																					label={rollupLabel}
																					size="small"
																					variant="outlined"
																					sx={{
																						height: 24,
																						fontSize: '1.1rem',
																						color: rollupColor,
																						borderColor: rollupColor,
																						flexShrink: 0,
																					}}
																				/>
																			</Tooltip>
																		);
																	})()}
																	{/* Status chip */}
																	<Tooltip
																		title={milestoneStatusTooltip || ''}
																		arrow
																		disableHoverListener={!milestoneStatusTooltip}>
																		<Chip
																			label={milestoneStatusLabel}
																			size="small"
																			sx={{
																				bgcolor: alpha(milestoneStatusColor, 0.1),
																				color: milestoneStatusColor,
																				fontWeight: 600,
																				fontSize: '1.25rem',
																				height: 28,
																			}}
																		/>
																	</Tooltip>
																</Stack>
																{/* ─── Expanded detail panel ─── */}
																<Collapse in={isExpanded}>
																	<Box
																		sx={{
																			borderLeft: `3px solid ${alpha(milestoneStatusColor, 0.25)}`,
																			ml: '33px',
																			borderRadius: '0 0 0 8px',
																			py: 0.5,
																		}}>
																		{/* ── Invoices ── */}
																		{(invoicesByMilestoneNumber.get(condition.id) || [])
																			.length > 0 && (
																			<Box sx={{ px: 2, pt: 0.75, pb: 0.25 }}>
																				<Stack spacing={1}>
																					{(
																						invoicesByMilestoneNumber.get(
																							condition.id
																						) || []
																					).map((inv) => (
																						<CardInvoiceDetails key={inv.id} invoice={inv} />
																					))}
																				</Stack>
																			</Box>
																		)}
																		{/* ── Vouchers ── */}
																		{conditionVouchers.length > 0 && (
																			<Box>
																				<Box
																					sx={{
																						display: 'flex',
																						alignItems: 'center',
																						gap: 0.75,
																						px: 2,
																						pt: 0.75,
																						pb: 0.25,
																					}}>
																					<Typography
																						sx={{
																							fontSize: '1.1rem',
																							fontWeight: 700,
																							color: theme.palette.grey[400],
																							textTransform: 'uppercase',
																							letterSpacing: '0.1em',
																						}}>
																						Vouchers
																					</Typography>
																					<Typography
																						sx={{
																							fontSize: '1.1rem',
																							fontWeight: 700,
																							color: theme.palette.grey[400],
																						}}>
																						{conditionVouchers.length}
																					</Typography>
																				</Box>
																				{conditionVouchers
																					.sort(
																						(a, b) =>
																							new Date(b.created).getTime() -
																							new Date(a.created).getTime()
																					)
																					.map((voucher) => {
																						const vIsPaid = voucher.status === 'REDEEMED';
																						const vIsCancelled = voucher.status === 'CANCELLED';
																						const recipientName =
																							typeof voucher.recipientEntity === 'object' &&
																							voucher.recipientEntity?.name
																								? voucher.recipientEntity.name
																								: null;
																						const contractAmountForCalc = milestoneBasis;
																						const voucherAmount =
																							voucher.amount ||
																							condition.paymentAmount ||
																							(condition.paymentPercentage
																								? (contractAmountForCalc *
																										condition.paymentPercentage) /
																									100
																								: 0);
																						const vStatusColor = vIsPaid
																							? COLORS.success.main
																							: vIsCancelled
																								? COLORS.error.main
																								: voucher.status === 'VERIFIED'
																									? COLORS.info.main
																									: voucher.status === 'READY'
																										? GRADIENTS.condition.icon
																										: COLORS.warning.main;
																						const vSupplierState = getSupplierVoucherState(voucher);
																						const vStatusLabel = vIsPaid
																							? 'Paid'
																							: vIsCancelled
																								? 'Cancelled'
																								: voucher.status === 'VERIFIED'
																									? 'Awaiting Review'
																									: voucher.status === 'READY'
																										? 'Awaiting Payment'
																										: vSupplierState === 'SUBMITTED'
																											? 'Supplier Submitted'
																											: vSupplierState === 'DRAFT'
																												? 'Supplier Draft'
																												: 'Processing';
																						return (
																							<Stack
																								key={voucher.id}
																								direction="row"
																								alignItems="center"
																								spacing={1}
																								onClick={() => handleOpenVoucherPage(voucher.id)}
																								sx={{
																									cursor: 'pointer',
																									py: 0.5,
																									px: 2,
																									pl: 3,
																									'&:hover': {
																										bgcolor: alpha(GRADIENTS.financial.icon, 0.04),
																									},
																								}}>
																								<Tooltip title={vStatusLabel} arrow>
																									<Box
																										sx={{
																											width: 8,
																											height: 8,
																											borderRadius: '50%',
																											bgcolor: vStatusColor,
																											flexShrink: 0,
																										}}
																									/>
																								</Tooltip>
																								<Typography
																									sx={{
																										fontWeight: 500,
																										fontSize: '1.25rem',
																										color: '#374151',
																										flex: 1,
																										minWidth: 0,
																									}}
																									noWrap>
																									Voucher #{voucher.id}
																									{recipientName ? ` · ${recipientName}` : ''}{' '}
																									<Typography
																										component="span"
																										sx={{ color: '#9CA3AF', fontSize: '1.25rem' }}>
																										{new Date(voucher.created).toLocaleDateString()}
																									</Typography>
																								</Typography>
																								<Box sx={{ textAlign: 'right', flexShrink: 0 }}>
																									<Typography
																										sx={{
																											fontWeight: 600,
																											fontSize: '1.25rem',
																											color: '#374151',
																										}}>
																										{formatCurrency({
																											currency:
																												voucher.currency || workPlanCurrency,
																											value: voucherAmount,
																										})}
																									</Typography>
																									{(voucher.currency || workPlanCurrency) ===
																										workPlanCurrency &&
																										reportingToLocalCaption(voucherAmount) && (
																											<Typography
																												sx={{
																													fontWeight: 500,
																													fontSize: '1.15rem',
																													color: '#6b7280',
																												}}>
																												{reportingToLocalCaption(voucherAmount)}
																											</Typography>
																										)}
																								</Box>
																								<Chip
																									label={vStatusLabel}
																									size="small"
																									sx={{
																										bgcolor: alpha(vStatusColor, 0.08),
																										color: vStatusColor,
																										fontWeight: 600,
																										fontSize: '1.25rem',
																										height: 24,
																										minWidth: 60,
																									}}
																								/>
																							</Stack>
																						);
																					})}
																			</Box>
																		)}

																		{/* ── Requirements Sections ── */}
																		{condition.verificationSections &&
																			condition.verificationSections.length > 0 &&
																			(() => {
																				const condResponses = responsesByCondition.get(
																					condition.id
																				);
																				return (
																					<Box>
																						<Divider sx={{ opacity: 0.15, mx: 2, my: 0.5 }} />
																						{condition.verificationSections.map((section, sIdx) => {
																							const assignee = projectUsers.find(
																								(u) => u.id === section.assignedTo
																							);
																							// Section status reflects the PIU's per-section
																							// verify/reject action written into voucher
																							// sectionResponses; falls back to the section
																							// definition's status (PENDING by default).
																							const effectiveSectionStatus =
																								sectionStatusByCondition
																									.get(condition.id)
																									?.get(section.id) ||
																								section.status ||
																								'PENDING';
																							const statusColor =
																								effectiveSectionStatus === 'VERIFIED'
																									? COLORS.success.main
																									: effectiveSectionStatus === 'REJECTED'
																										? theme.palette.error.main
																										: theme.palette.grey[500];
																							const sectionFields = section.fields || [];
																							const answeredCount = sectionFields.filter((f) => {
																								const r = condResponses?.get(f.id);
																								return r && (r.value !== null || r.documentId);
																							}).length;
																							return (
																								<Box key={section.id}>
																									<Box
																										sx={{
																											display: 'flex',
																											alignItems: 'center',
																											gap: 0.75,
																											px: 2,
																											pt: 0.75,
																											pb: 0.25,
																										}}>
																										<IconGroup
																											sx={{ fontSize: 16, color: 'primary.main' }}
																										/>
																										<Typography
																											sx={{
																												fontSize: '1.1rem',
																												fontWeight: 700,
																												color: theme.palette.grey[500],
																												textTransform: 'uppercase',
																												letterSpacing: '0.1em',
																											}}>
																											{section.name || `Section ${sIdx + 1}`}
																										</Typography>
																										{assignee && (
																											<Chip
																												label={`${assignee.firstName} ${assignee.lastName}`}
																												size="small"
																												variant="outlined"
																												sx={{ height: 24, fontSize: '1.1rem' }}
																											/>
																										)}
																										<Typography
																											sx={{
																												fontSize: '1.1rem',
																												fontWeight: 700,
																												color:
																													answeredCount === sectionFields.length &&
																													sectionFields.length > 0
																														? COLORS.success.main
																														: theme.palette.grey[500],
																											}}>
																											{answeredCount}/{sectionFields.length}
																										</Typography>
																										<Chip
																											label={getStatusLabel(
																												effectiveSectionStatus,
																												t
																											)}
																											size="small"
																											variant="outlined"
																											sx={{
																												height: 24,
																												fontSize: '1.1rem',
																												color: statusColor,
																												borderColor: statusColor,
																											}}
																										/>
																									</Box>
																									{sectionFields.map((field) => {
																										const response = condResponses?.get(field.id);
																										return (
																											<DataRequestFieldRow
																												key={field.id}
																												field={field}
																												response={response}
																												onDocClick={handleOpenDocViewer}
																												pl={3}
																											/>
																										);
																									})}
																								</Box>
																							);
																						})}
																					</Box>
																				);
																			})()}
																	</Box>
																</Collapse>
															</Box>
														);
													})}
											</Stack>
										</>
									)}
								</CardDetail>
							</Grid>

							{/* Right Column: Documents */}
							<Grid item xs={12} lg={4}>
								<CardDetail
									icon={IconAttachFile}
									title={t('documents')}
									subtitle={t('contract.documentCount', { count: contractDocs.length })}
									variant="contract"
									themeColor={GRADIENTS.contract.icon}
									onAdd={handleUploadDocument}
									tooltipAdd={t('contract.uploadDocument')}
									emptyMessage={t('noDocuments')}>
									{contractDocs.length > 0 && (
										<Stack spacing={0}>
											{contractDocs.map((doc) => {
												const isPdf =
													doc.doc_type === 'application/pdf' || doc.filename?.endsWith('.pdf');
												const uploaderName =
													typeof doc.uploaded_by === 'object' && doc.uploaded_by
														? doc.uploaded_by.displayName
														: null;
												const uploadDate = doc.uploaded_at
													? new Date(doc.uploaded_at).toLocaleDateString()
													: null;
												const tags = doc.metadata?.tags as string[] | undefined;
												const primaryTag = tags?.[0]
													? tags[0].charAt(0).toUpperCase() + tags[0].slice(1)
													: null;
												return (
													<Box key={doc.id}>
														<Divider />
														<Stack
															direction="row"
															alignItems="center"
															spacing={1.5}
															onClick={() => handleOpenDocViewer(String(doc.id), doc.filename)}
															sx={{
																py: 1.5,
																px: 2,
																cursor: 'pointer',
																'&:hover': { bgcolor: alpha(COLORS.primary.main, 0.04) },
															}}>
															<Box
																sx={{
																	width: 32,
																	height: 32,
																	borderRadius: '50%',
																	bgcolor: alpha(COLORS.primary.main, 0.08),
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'center',
																	flexShrink: 0,
																}}>
																{isPdf ? (
																	<IconPdf sx={{ fontSize: 16, color: '#E53935' }} />
																) : (
																	<IconContract sx={{ fontSize: 16, color: COLORS.primary.main }} />
																)}
															</Box>
															<Box sx={{ flex: 1, minWidth: 0 }}>
																<Typography
																	sx={{ fontWeight: 500, fontSize: '1.25rem', color: '#111827' }}
																	noWrap>
																	{doc.filename}
																</Typography>
																<Typography sx={{ color: '#6B7280', fontSize: '1.25rem' }} noWrap>
																	{[uploadDate, uploaderName, primaryTag]
																		.filter(Boolean)
																		.join(' · ')}
																</Typography>
															</Box>
														</Stack>
													</Box>
												);
											})}
										</Stack>
									)}
								</CardDetail>
							</Grid>
						</Grid>
					</>
				) : null}

				{/* Prepare for Payment is now rendered inline as a page above */}
				{/* Add/Edit Condition Form is now rendered inline as a page above */}

				{/* Document Viewer Dialog */}
				{viewerDoc && (
					<DialogDocumentViewer
						currentIndex={0}
						documents={[viewerDoc]}
						blobFilenames={[viewerBlobFilename]}
						documentInfos={viewerDocInfo ? [viewerDocInfo] : undefined}
						isOpen={viewerOpen}
						onClose={() => setViewerOpen(false)}
						onNavigate={() => {
							/* single doc, no-op */
						}}
					/>
				)}

				{/* Entity Documents Dialog */}
				{contract && (
					<DialogEntityDocuments
						isOpen={documentsDialogOpen}
						onClose={() => setDocumentsDialogOpen(false)}
						title="Contract Documents"
						sourceType="contract"
						sourceId={contractIdNumber}
						projectId={project?.id}
						conditionNamesById={conditionNamesById}
						fieldLabelsById={fieldLabelsById}
						onViewDocument={(doc) => {
							handleOpenDocViewer(doc.id, doc.filename);
						}}
					/>
				)}

				{/* Upload Document Dialog */}
				<DialogUploadDocument
					isOpen={searchParams.get('dialog') === 'uploadDocument'}
					onClose={() => {
						const params = new URLSearchParams(searchParams);
						params.delete('dialog');
						setSearchParams(params);
					}}
					onSuccess={() => {
						const params = new URLSearchParams(searchParams);
						params.delete('dialog');
						setSearchParams(params);
						fetchContractDocs();
						setBanner({
							message: 'Document uploaded successfully',
							severity: 'success',
						});
					}}
					projectId={project?.id}
					sourceType="contract"
					sourceId={contractIdNumber}
				/>

				{/* Add Invoice Dialog */}
				<DialogAddInvoice
					isOpen={searchParams.get('dialog') === 'addInvoice'}
					onClose={() => {
						const params = new URLSearchParams(searchParams);
						params.delete('dialog');
						params.delete('dialogContract');
						setSearchParams(params);
					}}
					onSuccess={() => {
						const params = new URLSearchParams(searchParams);
						params.delete('dialog');
						params.delete('dialogContract');
						setSearchParams(params);
						setBanner({
							message: 'Invoice submitted successfully',
							severity: 'success',
						});
					}}
					contractId={contract?.id ?? null}
					contractNum={contract ? getContractNum(contract) : ''}
					supplierName={
						contract?.entities?.find((e: { type?: string; name?: string }) => e.type === 'SUPPLIER')
							?.name || 'N/A'
					}
					contractCurrency={contract?.amounts?.[0]?.currency || 'USD'}
					contractAmount={milestoneBasis}
					milestones={paymentConditions.map((c) => ({
						id: c.id,
						name: c.name || `Milestone ${c.sequenceOrder || c.id}`,
						percentage: c.paymentPercentage || 0,
						amount:
							c.paymentAmount ||
							(c.paymentPercentage ? (milestoneBasis * c.paymentPercentage) / 100 : 0),
					}))}
					projectId={project?.id}
				/>

				{/* --- Maker-Checker Dialogs --- */}

				{/* Submit for Approval Confirmation */}
				<DialogActions
					isOpen={showSubmitDialog}
					onClose={() => setShowSubmitDialog(false)}
					maxWidth={480}
					title={{ children: t('contract.submitContractForApproval') }}
					subtitle={{
						children: `Submit "${contract ? getContractNum(contract) : ''}" for review and approval?`,
					}}
					actions={[
						{
							children: 'Cancel',
							color: 'inherit',
							disabled: isSubmitting,
							onClick: () => setShowSubmitDialog(false),
							variant: 'text',
						},
						{
							children: isSubmitting ? t('contract.submitting') : t('submitForApproval'),
							disabled: isSubmitting,
							loading: isSubmitting,
							onClick: handleSubmitForApproval,
							variant: 'contained',
						},
					]}>
					<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
						Once submitted, the contract will be locked for editing until a reviewer takes action.
					</Typography>
				</DialogActions>

				{/* Approve Confirmation */}
				<DialogActions
					isOpen={showApproveDialog}
					onClose={() => setShowApproveDialog(false)}
					maxWidth={480}
					title={{ children: t('contract.approveContract') }}
					subtitle={{
						children: `Approve "${contract ? getContractNum(contract) : ''}" and activate it?`,
					}}
					actions={[
						{
							children: 'Cancel',
							color: 'inherit',
							disabled: isSubmitting,
							onClick: () => setShowApproveDialog(false),
							variant: 'text',
						},
						{
							children: isSubmitting ? t('contract.approving') : t('approve'),
							color: 'success',
							disabled: isSubmitting,
							loading: isSubmitting,
							onClick: handleApprove,
							variant: 'contained',
						},
					]}>
					<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
						Once approved, the contract will become active and suppliers will be notified.
					</Typography>
				</DialogActions>

				{/* Recall Contract Dialog */}
				<DialogActions
					isOpen={showReturnDialog}
					onClose={() => {
						setShowReturnDialog(false);
						setRejectionReason('');
					}}
					maxWidth={480}
					title={{ children: t('contract.recallContract') }}
					subtitle={{
						children: `Recall "${contract ? getContractNum(contract) : ''}" to Draft status?`,
					}}
					actions={[
						{
							children: 'Cancel',
							color: 'inherit',
							disabled: isSubmitting,
							onClick: () => {
								setShowReturnDialog(false);
								setRejectionReason('');
							},
							variant: 'text',
						},
						{
							children: isSubmitting ? t('contract.recalling') : t('recall'),
							color: 'warning',
							disabled: isSubmitting || !rejectionReason.trim(),
							loading: isSubmitting,
							onClick: handleReturn,
							variant: 'contained',
						},
					]}>
					<Typography sx={{ mt: 1, mb: 2, color: '#374151', fontSize: '1.35rem' }}>
						Provide a reason for recalling this contract. The maker will be able to address the
						issues and resubmit.
					</Typography>
					<TextField
						autoFocus
						fullWidth
						label="Reason for return"
						multiline
						rows={3}
						value={rejectionReason}
						onChange={(e) => setRejectionReason(e.target.value)}
						placeholder="Provide feedback on what needs to change..."
						inputProps={{ maxLength: 2000 }}
					/>
				</DialogActions>

				{/* Mark Completed Confirmation */}
				<DialogActions
					isOpen={showCompleteDialog}
					onClose={() => setShowCompleteDialog(false)}
					maxWidth={480}
					title={{ children: t('contract.markContractCompleted') }}
					subtitle={{
						children: `Mark "${contract ? getContractNum(contract) : ''}" as completed?`,
					}}
					actions={[
						{
							children: 'Cancel',
							color: 'inherit',
							disabled: isSubmitting,
							onClick: () => setShowCompleteDialog(false),
							variant: 'text',
						},
						{
							children: isSubmitting ? t('contract.completing') : t('contract.markCompleted'),
							color: 'success',
							disabled: isSubmitting,
							loading: isSubmitting,
							onClick: handleComplete,
							variant: 'contained',
						},
					]}>
					<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
						Once completed, no further payments or milestone changes are allowed on this contract.
					</Typography>
				</DialogActions>

				{/* Cancel Contract Confirmation */}
				<DialogActions
					isOpen={showCancelDialog}
					onClose={() => setShowCancelDialog(false)}
					maxWidth={480}
					title={{ children: t('contract.cancelContract') }}
					subtitle={{
						children: `Cancel "${contract ? getContractNum(contract) : ''}"?`,
					}}
					actions={[
						{
							children: t('contract.keepActive'),
							color: 'inherit',
							disabled: isSubmitting,
							onClick: () => setShowCancelDialog(false),
							variant: 'text',
						},
						{
							children: isSubmitting ? t('contract.cancelling') : t('contract.cancelContract'),
							color: 'error',
							disabled: isSubmitting,
							loading: isSubmitting,
							onClick: handleCancelContract,
							variant: 'contained',
						},
					]}>
					<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
						Cancelling stops all further activity on this contract. You can reopen it as a draft
						later if you need to make changes.
					</Typography>
				</DialogActions>

				{/* Reopen as Draft / Edit Contract Confirmation */}
				<DialogActions
					isOpen={showReopenDialog}
					onClose={() => setShowReopenDialog(false)}
					maxWidth={480}
					title={{
						children: isApproved(contract?.status)
							? t('contract.editContract')
							: t('contract.reopenContractAsDraft'),
					}}
					subtitle={{
						children: isApproved(contract?.status)
							? `Edit "${contract ? getContractNum(contract) : ''}"? This will revert its status to Draft.`
							: `Reopen "${contract ? getContractNum(contract) : ''}" for editing?`,
					}}
					actions={[
						{
							children: 'Cancel',
							color: 'inherit',
							disabled: isSubmitting,
							onClick: () => setShowReopenDialog(false),
							variant: 'text',
						},
						{
							children: isSubmitting
								? isApproved(contract?.status)
									? t('contract.reverting')
									: t('contract.reopening')
								: isApproved(contract?.status)
									? t('contract.revertToDraft')
									: t('contract.reopenAsDraft'),
							disabled: isSubmitting,
							loading: isSubmitting,
							onClick: handleReopen,
							variant: 'contained',
						},
					]}>
					<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
						{t(
							'contract.revertToDraftWarning',
							'The contract will return to DRAFT status. Any changes will need to go back through the review and approval workflow before it becomes active again.'
						)}
					</Typography>
				</DialogActions>

				{/* Deactivate Contract Confirmation */}
				<DialogActions
					isOpen={showDeleteDialog}
					onClose={() => setShowDeleteDialog(false)}
					maxWidth={480}
					title={{ children: t('contract.deactivateContract') }}
					subtitle={{
						children: `Deactivate "${contract ? getContractNum(contract) : ''}"?`,
					}}
					actions={[
						{
							children: 'Cancel',
							color: 'inherit',
							disabled: isSubmitting,
							onClick: () => setShowDeleteDialog(false),
							variant: 'text',
						},
						{
							children: isSubmitting
								? t('contract.deactivating')
								: t('contract.deactivateContract'),
							color: 'error',
							disabled: isSubmitting,
							loading: isSubmitting,
							onClick: handleDeleteContract,
							variant: 'contained',
						},
					]}>
					<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
						This will remove the contract from the spend plan. Deactivation is blocked if any
						payments have been made or are in flight against this contract. This action cannot be
						undone.
					</Typography>
				</DialogActions>
			</Box>

			<DialogAuditTimeline
				isOpen={auditTimelineOpen}
				onClose={() => setAuditTimelineOpen(false)}
				title="Contract Audit Log"
				activities={auditActivities}
				isLoading={auditLoading}
			/>
		</Box>
	);
};
